// =============================================
// App.js – Blok A
// IMPORTY • FIREBASE • KONSTANTY • UTILITY FUNKCE
// =============================================

import React, { useEffect, useMemo, useState } from 'react';
import { initializeApp } from 'firebase/app';
import {
  getAuth,
  onAuthStateChanged,
  signInWithEmailAndPassword, signInWithCustomToken,
  signOut,
  EmailAuthProvider,
  reauthenticateWithCredential,
  updatePassword,
} from 'firebase/auth';
import {
  getFirestore,
  collection,
  doc,
  addDoc,
  setDoc,
  getDoc,
  onSnapshot,
  query,
  where,
  orderBy,
  serverTimestamp,
  updateDoc,
  Timestamp,
  enableNetwork,
  
} from 'firebase/firestore';

// ---------------------------------------------
// Firebase konfigurace (ENV proměnné)
// ---------------------------------------------
const firebaseConfig = {
  apiKey: process.env.REACT_APP_API_KEY,
  authDomain: process.env.REACT_APP_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_PROJECT_ID,
  storageBucket: process.env.REACT_APP_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_APP_ID,
};

const app = initializeApp(firebaseConfig);
export const auth = getAuth(app);
export const db = getFirestore(app);

// ---------------------------------------------
// Pracovní doba + pauza (požadavek)
//  • pracovní den: 08:30–12:00 a 12:30–17:00
//  • pauza na oběd: 12:00–12:30 (nezapočítává se)
// ---------------------------------------------
export const WORK_START_HOUR = 8; // 8:30
export const WORK_START_MIN = 30;
export const WORK_END_HOUR = 17;
export const WORK_END_MIN = 0;
export const LUNCH_START_HOUR = 12;
export const LUNCH_START_MIN = 0;
export const LUNCH_END_HOUR = 12;
export const LUNCH_END_MIN = 30;

// Denní fond (bez pauzy): 8 hodin
export const VACATION_HOURS_PER_WORKDAY =
  ((WORK_END_HOUR * 60 + WORK_END_MIN) - (WORK_START_HOUR * 60 + WORK_START_MIN) -
    (LUNCH_END_HOUR * 60 + LUNCH_END_MIN - (LUNCH_START_HOUR * 60 + LUNCH_START_MIN))) /
  60;

// ---------------------------------------------
// Utility – čas
// ---------------------------------------------
export const parseHM = (hm) => {
  if (!hm) return { h: WORK_START_HOUR, m: WORK_START_MIN };
  const [h, m] = hm.split(':').map(Number);
  return { h, m };
};

export const makeDate = (baseDate, h, m) =>
  new Date(baseDate.getFullYear(), baseDate.getMonth(), baseDate.getDate(), h, m, 0, 0);

export const isWeekend = (d) => d.getDay() === 0 || d.getDay() === 6;

export const clipToMonth = (start, end, y, m) => {
  const monthStart = new Date(y, m - 1, 1, 0, 0, 0, 0);
  const monthEnd = new Date(y, m, 0, 23, 59, 59, 999);
  const s = new Date(Math.max(start.getTime(), monthStart.getTime()));
  const e = new Date(Math.min(end.getTime(), monthEnd.getTime()));
  if (e < s) return null;
  return { s, e };
};

export const workIntervalForDate = (d) => ({
  start: makeDate(d, WORK_START_HOUR, WORK_START_MIN),
  lunchStart: makeDate(d, LUNCH_START_HOUR, LUNCH_START_MIN),
  lunchEnd: makeDate(d, LUNCH_END_HOUR, LUNCH_END_MIN),
  end: makeDate(d, WORK_END_HOUR, WORK_END_MIN),
});

export const diffHours = (ms) => ms / (1000 * 60 * 60);


// Čisté odpracované hodiny mezi dvěma časy v JEDNOM dni
// – klipuje na pracovní intervaly 8:30–12:00 a 12:30–17:00
// – pauza 12:00–12:30 se nepočítá
export const computeWorkedHours = (startInput, endInput) => {
  if (!startInput || !endInput) return 0;

  const s = new Date(startInput);
  const e = new Date(endInput);
  if (isNaN(s) || isNaN(e) || e <= s) return 0;

  // Pracovní intervaly pro daný den (podle 's')
  const { start, lunchStart, lunchEnd, end } = workIntervalForDate(s);

  // Intersekce [s, e] se segmenty práce (bez pauzy)
  const seg1Start = Math.max(s.getTime(), start.getTime());
  const seg1End   = Math.min(e.getTime(), lunchStart.getTime());

  const seg2Start = Math.max(s.getTime(), lunchEnd.getTime());
  const seg2End   = Math.min(e.getTime(), end.getTime());

  let totalMs = 0;
  if (seg1End > seg1Start) totalMs += seg1End - seg1Start; // 8:30–12:00
  if (seg2End > seg2Start) totalMs += seg2End - seg2Start; // 12:30–17:00

  return diffHours(totalMs);
};

// Odpracované hodiny pro interval, který může přesahovat přes více dní.
// Každý pracovní den aplikuje pracovní okna 8:30–12:00 a 12:30–17:00 (bez pauzy).
export const computeWorkedHoursSpan = (startInput, endInput) => {
  if (!startInput || !endInput) return 0;
  const s0 = new Date(startInput);
  const e0 = new Date(endInput);
  if (isNaN(s0) || isNaN(e0) || e0 <= s0) return 0;

  let total = 0;
  let d = new Date(s0.getFullYear(), s0.getMonth(), s0.getDate());
  const last = new Date(e0.getFullYear(), e0.getMonth(), e0.getDate());

  while (d <= last) {
    const dayStart = new Date(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0, 0);
    const dayEnd   = new Date(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59, 999);

    const s = new Date(Math.max(s0.getTime(), dayStart.getTime()));
    const e = new Date(Math.min(e0.getTime(), dayEnd.getTime()));

    if (e > s) total += computeWorkedHours(s, e);

    d.setDate(d.getDate() + 1);
  }
  return total;
};


// Přesné hodiny dovolené (full/half/hours) v rámci vybraného měsíce
export const computeLeaveHoursInMonth = (req, y, m) => {
  const fullStart = req.startDate.toDate();
  const fullEnd = req.endDate.toDate();
  const clipped = clipToMonth(fullStart, fullEnd, y, m);
  if (!clipped) return 0;

  const hasTimes = req.leaveType === 'hours' && req.startTime && req.endTime;
  const { h: stH, m: stM } = hasTimes ? parseHM(req.startTime) : { h: WORK_START_HOUR, m: WORK_START_MIN };
  const { h: enH, m: enM } = hasTimes ? parseHM(req.endTime) : { h: WORK_END_HOUR, m: WORK_END_MIN };

  let totalHours = 0;
  let d = new Date(clipped.s.getFullYear(), clipped.s.getMonth(), clipped.s.getDate());
  const last = new Date(clipped.e.getFullYear(), clipped.e.getMonth(), clipped.e.getDate());

  while (d <= last) {
    if (!isWeekend(d)) {
        if (req.leaveType === 'half-day') {
            totalHours += 4; // Přesně 4 hodiny za půlden
        } else {
            const { start, end } = workIntervalForDate(d);
            let dayStart = start;
            let dayEnd = end;

            if (req.leaveType === 'hours') {
                const isFirstDay = d.toDateString() === fullStart.toDateString();
                const isLastDay = d.toDateString() === fullEnd.toDateString();
                if (isFirstDay) dayStart = makeDate(d, stH, stM);
                if (isLastDay) dayEnd = makeDate(d, enH, enM);
            }

            const h = computeWorkedHours(dayStart, dayEnd);
            if (h > 0) totalHours += h;
        }
    }
    d.setDate(d.getDate() + 1);
  }

  return totalHours;
};

// Spočítá celkový počet hodin dovolené pro danou žádost
export const computeTotalLeaveHours = (req) => {
  if (req.status !== 'approved') return 0;

  const fullStart = req.startDate.toDate();
  const fullEnd = req.endDate.toDate();

  const hasTimes = req.leaveType === 'hours' && req.startTime && req.endTime;
  const { h: stH, m: stM } = hasTimes ? parseHM(req.startTime) : { h: WORK_START_HOUR, m: WORK_START_MIN };
  const { h: enH, m: enM } = hasTimes ? parseHM(req.endTime) : { h: WORK_END_HOUR, m: WORK_END_MIN };

  let totalHours = 0;
  let d = new Date(fullStart.getFullYear(), fullStart.getMonth(), fullStart.getDate());
  const last = new Date(fullEnd.getFullYear(), fullEnd.getMonth(), fullEnd.getDate());

  while (d <= last) {
    if (!isWeekend(d)) {
      if (req.leaveType === 'half-day') {
        totalHours += 4; // Přesně 4 hodiny za půlden
      } else {
        const { start, end } = workIntervalForDate(d);
        let dayStart = start;
        let dayEnd = end;

        if (req.leaveType === 'hours') {
            const isFirstDay = d.toDateString() === fullStart.toDateString();
            const isLastDay = d.toDateString() === fullEnd.toDateString();
            if (isFirstDay) dayStart = makeDate(d, stH, stM);
            if (isLastDay) dayEnd = makeDate(d, enH, enM);
        }
        
        const h = computeWorkedHours(dayStart, dayEnd);
        if (h > 0) totalHours += h;
      }
    }
    d.setDate(d.getDate() + 1);
  }

  return totalHours;
};


// ---------------------------------------------
// Utility – CSV export + formátování datumu
// ---------------------------------------------
export const exportToCsv = (data, filename, headers) => {
  const csvContent =
    'data:text/csv;charset=utf-8,' +
    [headers.join(','), ...data.map((row) => headers.map((h) => JSON.stringify(row[h] ?? '')).join(','))].join('\n');
  const encodedUri = encodeURI(csvContent);
  const link = document.createElement('a');
  link.setAttribute('href', encodedUri);
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

export const formatDate = (timestamp) =>
  timestamp
    ? timestamp.toDate().toLocaleString('cs-CZ', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    : 'N/A';

export const formatDateOnly = (timestamp) =>
  timestamp ? timestamp.toDate().toLocaleDateString('cs-CZ') : 'N/A';

// ---------------------------------------------
// Pomocné parsování YYYY-MM (pro admin měsíce)
// ---------------------------------------------
export const parseMonthParts = (ym) => {
  const [y, m] = ym.split('-').map(Number);
  return { y, m };
};
// =============================================
// App.js – Blok B
// UI KOMPONENTY: IKONY • ŠTÍTKY • MODÁLY • VIZUALIZACE • KALENDÁŘ
// (Vše patří do stejného App.js – zde odděleno logicky)
// =============================================



/*********************************
 * IKONY (SVG)         *
 *********************************/
export const ClockIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
);
export const CalendarIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
);
export const HomeIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>
);
export const LogInIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4"/><polyline points="10 17 15 12 10 7"/><line x1="15" y1="12" x2="3" y2="12"/></svg>
);
export const LogOutIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>
);
export const UserIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
);
export const MedicalIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 12h-4l-3 9L9 3l-3 9H2"/></svg>
);
export const PieChartIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21.21 15.89A10 10 0 1 1 8.11 2.99"/><path d="M22 12A10 10 0 0 0 12 2v10z"/></svg>
);
export const CheckCircleIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>
);
export const XCircleIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/></svg>
);
export const PendingIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
);
export const DownloadIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
);

/*********************************
 * Stavový štítek (Status)  *
 *********************************/
export const StatusBadge = ({ status }) => {
  const map = {
    pending:  { text: 'Čeká na schválení', className: 'bg-yellow-100 text-yellow-800', Icon: PendingIcon },
    approved: { text: 'Schváleno',          className: 'bg-green-100 text-green-800',  Icon: CheckCircleIcon },
    rejected: { text: 'Zamítnuto',          className: 'bg-red-100 text-red-800',      Icon: XCircleIcon },
  };
  const c = map[status] || map.pending;
  return (
    <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium whitespace-nowrap ${c.className}`}>
      <c.Icon />
      <span className="ml-2 leading-none">{c.text}</span>
    </span>
  );
};

/*********************************
 * Vizualizace měsíčního fondu  *
 *********************************/
export const WorkFundVisualizer = ({ officeRecords, homeOfficeRecords }) => {
  const data = useMemo(() => {
    const now = new Date();
    const year = now.getFullYear();
    const month = now.getMonth();

    const getWorkdaysInMonth = (y, m) => {
      let wd = 0;
      const d = new Date(y, m, 1);
      while (d.getMonth() === m) {
        const dow = d.getDay();
        if (dow !== 0 && dow !== 6) wd++;
        d.setDate(d.getDate() + 1);
      }
      return wd;
    };

    const calcHours = (recs) => {
      return recs
        .filter((r) => {
          const dt = r.timestampIn?.toDate?.();
          return dt && r.timestampOut && dt.getFullYear() === year && dt.getMonth() === month;
        })
        .reduce((acc, r) => {
          const s = r.timestampIn.toDate();
          const e = r.timestampOut.toDate();
          return acc + computeWorkedHoursSpan(s, e);
        }, 0);
    };

    const required = getWorkdaysInMonth(year, month) * 8; // pauza už je zahrnuta v denním fondu
    const officeH = calcHours(officeRecords);
    const hoH = calcHours(homeOfficeRecords);
    const total = officeH + hoH;

    return {
      required,
      officeH,
      hoH,
      total,
      officePct: required ? (officeH / required) * 100 : 0,
      hoPct: required ? (hoH / required) * 100 : 0,
      fulfill: required ? (total / required) * 100 : 0,
    };
  }, [officeRecords, homeOfficeRecords]);

  return (
    <div className="bg-white shadow rounded-lg p-6">
      <h3 className="text-lg font-medium leading-6 text-gray-900 flex items-center">
        <PieChartIcon />
        <span className="ml-2">Měsíční fond pracovní doby</span>
      </h3>
      <div className="mt-4">
        <div className="flex justify-between text-sm font-medium text-gray-600">
          <span>Odpracováno: {data.total.toFixed(1)}h</span>
          <span>Fond: {data.required}h</span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-6 mt-2 flex overflow-hidden border border-gray-300">
          <div className="bg-green-500 h-full transition-all duration-500" style={{ width: `${data.officePct}%` }} title={`V kanceláři: ${data.officeH.toFixed(1)}h`} />
          <div className="bg-blue-500 h-full transition-all duration-500" style={{ width: `${data.hoPct}%` }} title={`Home Office: ${data.hoH.toFixed(1)}h`} />
        </div>
        <div className="mt-3 flex justify-between text-sm text-gray-500">
          <div className="flex items-center"><span className="h-3 w-3 bg-green-500 rounded-full mr-2"/>V kanceláři</div>
          <div className="flex items-center"><span className="h-3 w-3 bg-blue-500 rounded-full mr-2"/>Home Office</div>
          <span className="font-bold text-gray-700">Splněno: {data.fulfill.toFixed(0)}%</span>
        </div>
      </div>
    </div>
  );
};

/*********************************
 * EditRequestModal - Editace pending žádosti *
 *********************************/
export const EditRequestModal = ({ isOpen, onClose, onSave, request }) => {
  const [formData, setFormData] = useState({});

  useEffect(() => {
    if (isOpen && request) {
      // Předvyplnění formuláře podle typu žádosti
      if (request.coll === 'leave_requests') {
        setFormData({
          leaveType: request.raw.leaveType || 'full-day',
          halfDayPart: request.raw.halfDayPart || 'AM',
          startDate: request.raw.startDate?.toDate ? request.raw.startDate.toDate().toISOString().split('T')[0] : '',
          endDate: request.raw.endDate?.toDate ? request.raw.endDate.toDate().toISOString().split('T')[0] : '',
          startTime: request.raw.startTime || '08:30',
          endTime: request.raw.endTime || '17:00',
        });
      } else if (request.coll === 'home_office_requests') {
        setFormData({
          startDate: request.raw.startDate?.toDate ? request.raw.startDate.toDate().toISOString().split('T')[0] : '',
          endDate: request.raw.endDate?.toDate ? request.raw.endDate.toDate().toISOString().split('T')[0] : '',
        });
      } else if (request.coll === 'doctor_visits') {
        setFormData({
          visitDate: request.raw.visitDate?.toDate ? request.raw.visitDate.toDate().toISOString().split('T')[0] : '',
          isFullDay: request.raw.isFullDay || false,
          visitStartTime: request.raw.visitStartTime || '',
          visitEndTime: request.raw.visitEndTime || '',
          reason: request.raw.reason || '',
        });
      }
    }
  }, [isOpen, request]);

  if (!isOpen || !request) return null;

  const handleSave = () => {
    let updatedData = {};
    if (request.coll === 'leave_requests') {
      updatedData = {
        leaveType: formData.leaveType,
        halfDayPart: formData.leaveType === 'half-day' ? formData.halfDayPart : null,
        startDate: Timestamp.fromDate(new Date(formData.startDate)),
        endDate: Timestamp.fromDate(new Date(formData.endDate)),
        startTime: formData.leaveType === 'hours' ? formData.startTime : null,
        endTime: formData.leaveType === 'hours' ? formData.endTime : null,
      };
    } else if (request.coll === 'home_office_requests') {
      updatedData = {
        startDate: Timestamp.fromDate(new Date(formData.startDate)),
        endDate: Timestamp.fromDate(new Date(formData.endDate)),
      };
    } else if (request.coll === 'doctor_visits') {
      updatedData = {
        visitDate: Timestamp.fromDate(new Date(formData.visitDate)),
        isFullDay: formData.isFullDay,
        visitStartTime: formData.isFullDay ? null : formData.visitStartTime,
        visitEndTime: formData.isFullDay ? null : formData.visitEndTime,
        reason: formData.reason,
      };
    }
    onSave(request.coll, request.id, updatedData);
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center">
      <div className="relative mx-auto p-5 border w-full max-w-2xl shadow-lg rounded-md bg-white">
        <h3 className="text-lg font-medium text-gray-900">Upravit žádost</h3>
        <div className="mt-4 space-y-4">
          {request.coll === 'leave_requests' && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700">Typ dovolené</label>
                <select value={formData.leaveType} onChange={(e) => setFormData({...formData, leaveType: e.target.value})} className="mt-1 block w-full border-gray-300 rounded-md shadow-sm py-2 px-3">
                  <option value="full-day">Celý den</option>
                  <option value="half-day">Půl dne</option>
                  <option value="hours">Hodiny</option>
                </select>
              </div>
              {formData.leaveType === 'half-day' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700">Část dne</label>
                  <select value={formData.halfDayPart} onChange={(e) => setFormData({...formData, halfDayPart: e.target.value})} className="mt-1 block w-full border-gray-300 rounded-md shadow-sm py-2 px-3">
                    <option value="AM">Dopoledne</option>
                    <option value="PM">Odpoledne</option>
                  </select>
                </div>
              )}
              {formData.leaveType === 'hours' && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Od</label>
                    <input type="time" value={formData.startTime} onChange={(e) => setFormData({...formData, startTime: e.target.value})} className="mt-1 block w-full border-gray-300 rounded-md shadow-sm py-2 px-3" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Do</label>
                    <input type="time" value={formData.endTime} onChange={(e) => setFormData({...formData, endTime: e.target.value})} className="mt-1 block w-full border-gray-300 rounded-md shadow-sm py-2 px-3" />
                  </div>
                </div>
              )}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Od (datum)</label>
                  <input type="date" value={formData.startDate} onChange={(e) => setFormData({...formData, startDate: e.target.value})} className="mt-1 block w-full border-gray-300 rounded-md shadow-sm py-2 px-3" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Do (datum)</label>
                  <input type="date" value={formData.endDate} onChange={(e) => setFormData({...formData, endDate: e.target.value})} className="mt-1 block w-full border-gray-300 rounded-md shadow-sm py-2 px-3" />
                </div>
              </div>
            </>
          )}
          {request.coll === 'home_office_requests' && (
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Od</label>
                <input type="date" value={formData.startDate} onChange={(e) => setFormData({...formData, startDate: e.target.value})} className="mt-1 block w-full border-gray-300 rounded-md shadow-sm py-2 px-3" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Do</label>
                <input type="date" value={formData.endDate} onChange={(e) => setFormData({...formData, endDate: e.target.value})} className="mt-1 block w-full border-gray-300 rounded-md shadow-sm py-2 px-3" />
              </div>
            </div>
          )}
          {request.coll === 'doctor_visits' && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700">Datum návštěvy</label>
                <input type="date" value={formData.visitDate} onChange={(e) => setFormData({...formData, visitDate: e.target.value})} className="mt-1 block w-full border-gray-300 rounded-md shadow-sm py-2 px-3" />
              </div>
              <div className="flex items-center">
                <input type="checkbox" checked={formData.isFullDay} onChange={(e) => setFormData({...formData, isFullDay: e.target.checked})} className="h-4 w-4 text-indigo-600 border-gray-300 rounded" />
                <label className="ml-2 block text-sm text-gray-900">Celodenní</label>
              </div>
              {!formData.isFullDay && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Čas od</label>
                    <input type="time" value={formData.visitStartTime} onChange={(e) => setFormData({...formData, visitStartTime: e.target.value})} className="mt-1 block w-full border-gray-300 rounded-md shadow-sm py-2 px-3" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Čas do</label>
                    <input type="time" value={formData.visitEndTime} onChange={(e) => setFormData({...formData, visitEndTime: e.target.value})} className="mt-1 block w-full border-gray-300 rounded-md shadow-sm py-2 px-3" />
                  </div>
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-gray-700">Důvod</label>
                <textarea value={formData.reason} onChange={(e) => setFormData({...formData, reason: e.target.value})} rows={3} className="mt-1 block w-full border-gray-300 rounded-md shadow-sm py-2 px-3" />
              </div>
            </>
          )}
        </div>
        <div className="mt-4 flex justify-end space-x-3">
          <button onClick={onClose} className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300">Zrušit</button>
          <button onClick={handleSave} className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700">Uložit změny</button>
        </div>
      </div>
    </div>
  );
};

/*********************************
 * ModificationRequestModal - Žádost o změnu schválené žádosti *
 *********************************/
export const ModificationRequestModal = ({ isOpen, onClose, onSubmit, request, reason, setReason }) => {
  const [formData, setFormData] = useState({});

  useEffect(() => {
    if (isOpen && request) {
      // Předvyplnění stávajících hodnot
      if (request.coll === 'leave_requests') {
        setFormData({
          leaveType: request.raw.leaveType || 'full-day',
          halfDayPart: request.raw.halfDayPart || 'AM',
          startDate: request.raw.startDate?.toDate ? request.raw.startDate.toDate().toISOString().split('T')[0] : '',
          endDate: request.raw.endDate?.toDate ? request.raw.endDate.toDate().toISOString().split('T')[0] : '',
          startTime: request.raw.startTime || '08:30',
          endTime: request.raw.endTime || '17:00',
        });
      } else if (request.coll === 'home_office_requests') {
        setFormData({
          startDate: request.raw.startDate?.toDate ? request.raw.startDate.toDate().toISOString().split('T')[0] : '',
          endDate: request.raw.endDate?.toDate ? request.raw.endDate.toDate().toISOString().split('T')[0] : '',
        });
      } else if (request.coll === 'doctor_visits') {
        setFormData({
          visitDate: request.raw.visitDate?.toDate ? request.raw.visitDate.toDate().toISOString().split('T')[0] : '',
          isFullDay: request.raw.isFullDay || false,
          visitStartTime: request.raw.visitStartTime || '',
          visitEndTime: request.raw.visitEndTime || '',
          reason: request.raw.reason || '',
        });
      }
    }
  }, [isOpen, request]);

  if (!isOpen || !request) return null;

  const handleSubmit = () => {
    let requestedChanges = {};
    if (request.coll === 'leave_requests') {
      requestedChanges = {
        leaveType: formData.leaveType,
        halfDayPart: formData.leaveType === 'half-day' ? formData.halfDayPart : null,
        startDate: Timestamp.fromDate(new Date(formData.startDate)),
        endDate: Timestamp.fromDate(new Date(formData.endDate)),
        startTime: formData.leaveType === 'hours' ? formData.startTime : null,
        endTime: formData.leaveType === 'hours' ? formData.endTime : null,
      };
    } else if (request.coll === 'home_office_requests') {
      requestedChanges = {
        startDate: Timestamp.fromDate(new Date(formData.startDate)),
        endDate: Timestamp.fromDate(new Date(formData.endDate)),
      };
    } else if (request.coll === 'doctor_visits') {
      requestedChanges = {
        visitDate: Timestamp.fromDate(new Date(formData.visitDate)),
        isFullDay: formData.isFullDay,
        visitStartTime: formData.isFullDay ? null : formData.visitStartTime,
        visitEndTime: formData.isFullDay ? null : formData.visitEndTime,
        reason: formData.reason,
      };
    }
    onSubmit(request, requestedChanges);
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center">
      <div className="relative mx-auto p-5 border w-full max-w-2xl shadow-lg rounded-md bg-white">
        <h3 className="text-lg font-medium text-gray-900">Požádat o změnu schválené žádosti</h3>
        <p className="text-sm text-gray-600 mt-2">Upravte hodnoty níže a vyplňte důvod změny. Změna musí být schválena adminem.</p>
        <div className="mt-4 space-y-4">
          {request.coll === 'leave_requests' && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700">Typ dovolené</label>
                <select value={formData.leaveType} onChange={(e) => setFormData({...formData, leaveType: e.target.value})} className="mt-1 block w-full border-gray-300 rounded-md shadow-sm py-2 px-3">
                  <option value="full-day">Celý den</option>
                  <option value="half-day">Půl dne</option>
                  <option value="hours">Hodiny</option>
                </select>
              </div>
              {formData.leaveType === 'half-day' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700">Část dne</label>
                  <select value={formData.halfDayPart} onChange={(e) => setFormData({...formData, halfDayPart: e.target.value})} className="mt-1 block w-full border-gray-300 rounded-md shadow-sm py-2 px-3">
                    <option value="AM">Dopoledne</option>
                    <option value="PM">Odpoledne</option>
                  </select>
                </div>
              )}
              {formData.leaveType === 'hours' && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Od</label>
                    <input type="time" value={formData.startTime} onChange={(e) => setFormData({...formData, startTime: e.target.value})} className="mt-1 block w-full border-gray-300 rounded-md shadow-sm py-2 px-3" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Do</label>
                    <input type="time" value={formData.endTime} onChange={(e) => setFormData({...formData, endTime: e.target.value})} className="mt-1 block w-full border-gray-300 rounded-md shadow-sm py-2 px-3" />
                  </div>
                </div>
              )}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Od (datum)</label>
                  <input type="date" value={formData.startDate} onChange={(e) => setFormData({...formData, startDate: e.target.value})} className="mt-1 block w-full border-gray-300 rounded-md shadow-sm py-2 px-3" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Do (datum)</label>
                  <input type="date" value={formData.endDate} onChange={(e) => setFormData({...formData, endDate: e.target.value})} className="mt-1 block w-full border-gray-300 rounded-md shadow-sm py-2 px-3" />
                </div>
              </div>
            </>
          )}
          {request.coll === 'home_office_requests' && (
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Od</label>
                <input type="date" value={formData.startDate} onChange={(e) => setFormData({...formData, startDate: e.target.value})} className="mt-1 block w-full border-gray-300 rounded-md shadow-sm py-2 px-3" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Do</label>
                <input type="date" value={formData.endDate} onChange={(e) => setFormData({...formData, endDate: e.target.value})} className="mt-1 block w-full border-gray-300 rounded-md shadow-sm py-2 px-3" />
              </div>
            </div>
          )}
          {request.coll === 'doctor_visits' && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700">Datum návštěvy</label>
                <input type="date" value={formData.visitDate} onChange={(e) => setFormData({...formData, visitDate: e.target.value})} className="mt-1 block w-full border-gray-300 rounded-md shadow-sm py-2 px-3" />
              </div>
              <div className="flex items-center">
                <input type="checkbox" checked={formData.isFullDay} onChange={(e) => setFormData({...formData, isFullDay: e.target.checked})} className="h-4 w-4 text-indigo-600 border-gray-300 rounded" />
                <label className="ml-2 block text-sm text-gray-900">Celodenní</label>
              </div>
              {!formData.isFullDay && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Čas od</label>
                    <input type="time" value={formData.visitStartTime} onChange={(e) => setFormData({...formData, visitStartTime: e.target.value})} className="mt-1 block w-full border-gray-300 rounded-md shadow-sm py-2 px-3" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Čas do</label>
                    <input type="time" value={formData.visitEndTime} onChange={(e) => setFormData({...formData, visitEndTime: e.target.value})} className="mt-1 block w-full border-gray-300 rounded-md shadow-sm py-2 px-3" />
                  </div>
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-gray-700">Důvod návštěvy</label>
                <textarea value={formData.reason} onChange={(e) => setFormData({...formData, reason: e.target.value})} rows={2} className="mt-1 block w-full border-gray-300 rounded-md shadow-sm py-2 px-3" />
              </div>
            </>
          )}
          <div>
            <label className="block text-sm font-medium text-gray-700">Důvod změny (povinné)</label>
            <textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={3} className="mt-1 block w-full border-gray-300 rounded-md shadow-sm py-2 px-3" placeholder="Vysvětlete prosím, proč potřebujete změnit tuto žádost..." />
          </div>
        </div>
        <div className="mt-4 flex justify-end space-x-3">
          <button onClick={onClose} className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300">Zrušit</button>
          <button onClick={handleSubmit} className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700">Odeslat žádost o změnu</button>
        </div>
      </div>
    </div>
  );
};

/*********************************
 * NoteModal            *
 *********************************/
export const NoteModal = ({ isOpen, onClose, onSave, initialText }) => {
  const [note, setNote] = useState('');
  useEffect(() => {
    if (isOpen) setNote(initialText || '');
  }, [isOpen, initialText]);
  if (!isOpen) return null;
  const handleSave = () => {
    onSave(note);
    onClose();
  };
  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center">
      <div className="relative mx-auto p-5 border w-full max-w-md shadow-lg rounded-md bg-white">
        <h3 className="text-lg font-medium text-gray-900">Denní poznámka</h3>
        <div className="mt-4">
          <textarea rows={5} className="w-full p-2 border border-gray-300 rounded-md" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Co se dnes povedlo..." />
        </div>
        <div className="mt-4 flex justify-end space-x-3">
          <button onClick={onClose} className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300">Zrušit</button>
          <button onClick={handleSave} className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700">Uložit poznámku</button>
        </div>
      </div>
    </div>
  );
};

/*********************************
 * ProfileModal           *
 *********************************/
export const ProfileModal = ({ isOpen, onClose, user }) => {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  if (!isOpen) return null;

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    if (newPassword !== confirmPassword) {
      setError('Nová hesla se neshodují.');
      return;
    }
    if (!user) {
      setError('Nejste přihlášen.');
      return;
    }
    try {
      const credential = EmailAuthProvider.credential(user.email, currentPassword);
      await reauthenticateWithCredential(user, credential);
      await updatePassword(user, newPassword);
      setSuccess('Heslo bylo úspěšně změněno.');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setTimeout(onClose, 1500);
    } catch (err) {
      console.error(err);
      setError('Nepodařilo se změnit heslo. Zkontrolujte stávající heslo.');
    }
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center">
      <div className="relative mx-auto p-5 border w-full max-w-md shadow-lg rounded-md bg-white">
        <h3 className="text-lg font-medium text-gray-900">Změna hesla</h3>
        <form onSubmit={handleChangePassword} className="mt-4 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">Stávající heslo</label>
            <input type="password" value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)} required className="mt-1 block w-full border-gray-300 rounded-md shadow-sm py-2 px-3" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Nové heslo</label>
            <input type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} required className="mt-1 block w-full border-gray-300 rounded-md shadow-sm py-2 px-3" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Potvrdit nové heslo</label>
            <input type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} required className="mt-1 block w-full border-gray-300 rounded-md shadow-sm py-2 px-3" />
          </div>
          {error && <p className="text-red-500 text-sm">{error}</p>}
          {success && <p className="text-green-500 text-sm">{success}</p>}
          <div className="mt-4 flex justify-end space-x-3">
            <button type="button" onClick={onClose} className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300">Zrušit</button>
            <button type="submit" className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700">Změnit heslo</button>
          </div>
        </form>
      </div>
    </div>
  );
};

/*********************************
 * TeamCalendar         *
 *********************************/
export const TeamCalendar = ({ leave, homeOffice, doctor }) => {
  const [currentDate, setCurrentDate] = useState(new Date());

  // helper: YYYY-MM-DD podle lokálního času
  const dateKeyLocal = (d) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  };

  const events = useMemo(() => {
    const eventMap = new Map();
    const addEvent = (date, event) => {
      const dateString = dateKeyLocal(date);
      if (!eventMap.has(dateString)) eventMap.set(dateString, []);
      eventMap.get(dateString).push(event);
    };

    leave.forEach((req) => {
      let d = new Date(req.startDate.toDate());
      const end = req.endDate.toDate();
      while (d <= end) {
        addEvent(new Date(d), { type: 'leave', text: req.userName });
        d.setDate(d.getDate() + 1);
      }
    });

    homeOffice.forEach((req) => {
      let d = new Date(req.startDate.toDate());
      const end = req.endDate.toDate();
      while (d <= end) {
        addEvent(new Date(d), { type: 'ho', text: req.userName });
        d.setDate(d.getDate() + 1);
      }
    });

    doctor.forEach((req) => {
      addEvent(new Date(req.visitDate.toDate()), { type: 'doctor', text: req.userName });
    });

    return eventMap;
  }, [leave, homeOffice, doctor]);

  const month = currentDate.getMonth();
  const year = currentDate.getFullYear();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const firstDayOfMonth = new Date(year, month, 1).getDay(); // 0=Sun, 1=Mon,...
  const startOffset = firstDayOfMonth === 0 ? 6 : firstDayOfMonth - 1;
  const changeMonth = (offset) => setCurrentDate(new Date(year, month + offset, 1));
  const days = Array.from({ length: daysInMonth }, (_, i) => i + 1);
  const blanks = Array.from({ length: startOffset }, (_, i) => i);

  const eventColors = {
    leave: 'bg-yellow-200 text-yellow-800',
    ho: 'bg-blue-200 text-blue-800',
    doctor: 'bg-red-200 text-red-800',
  };

  return (
    <div className="bg-white shadow rounded-lg p-6">
      <div className="flex justify-between items-center mb-4">
        <button onClick={() => changeMonth(-1)} className="px-3 py-1 bg-gray-200 rounded-md">&lt;</button>
        <h3 className="text-lg font-medium text-gray-900 capitalize">
          {new Intl.DateTimeFormat('cs-CZ', { month: 'long', year: 'numeric' }).format(currentDate)}
        </h3>
        <button onClick={() => changeMonth(1)} className="px-3 py-1 bg-gray-200 rounded-md">&gt;</button>
      </div>
      <div className="grid grid-cols-7 gap-1 text-center text-sm">
        {['Po', 'Út', 'St', 'Čt', 'Pá', 'So', 'Ne'].map((day) => (
          <div key={day} className="font-bold text-gray-600">{day}</div>
        ))}
        {blanks.map((b) => (
          <div key={`blank-${b}`} />
        ))}
        {days.map((day) => {
          const dateString = dateKeyLocal(new Date(year, month, day));
          const dayEvents = events.get(dateString) || [];
          return (
            <div key={day} className="border rounded-md p-1 min-h-[100px] flex flex-col">
              <span className="font-semibold">{day}</span>
              <div className="flex-grow overflow-y-auto text-xs">
                {dayEvents.map((event, i) => (
                  <div key={i} className={`p-1 mt-1 rounded ${eventColors[event.type]}`}>{event.text}</div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
      <div className="mt-4 flex justify-center space-x-4 text-sm text-gray-500">
        <div className="flex items-center"><span className="h-3 w-3 rounded-full mr-2 bg-yellow-200 border border-yellow-300"/>Dovolená</div>
        <div className="flex items-center"><span className="h-3 w-3 rounded-full mr-2 bg-blue-200 border border-blue-300"/>Home Office</div>
        <div className="flex items-center"><span className="h-3 w-3 rounded-full mr-2 bg-red-200 border border-red-300"/>Lékař</div>
      </div>
    </div>
  );
};

// =============================================
// App.js – Blok C
// STAV APLIKACE • AUTENTIZACE • POSLUCHAČE • AKCE
// Vytváříme hook useAppLogic(), který vrací stav a handlery pro UI v Bloku D
// =============================================





export const useAppLogic = () => {
  // ---------------------------
  //     ZÁKLADNÍ STAV
  // ---------------------------
  const [user, setUser] = useState(null);
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Žádosti
  const [leaveRequests, setLeaveRequests] = useState([]);
  const [doctorVisits, setDoctorVisits] = useState([]);
  const [homeOfficeRequests, setHomeOfficeRequests] = useState([]);

  // Moje docházka
  const [myAttendance, setMyAttendance] = useState([]);
  const [myHomeOfficeRecords, setMyHomeOfficeRecords] = useState([]);
  const [currentAttendanceStatus, setCurrentAttendanceStatus] = useState({ status: 'out', recordId: null });
  const [currentHomeOfficeStatus, setCurrentHomeOfficeStatus] = useState({ status: 'finished', recordId: null });
  const [isHomeOfficeDay, setIsHomeOfficeDay] = useState(false);

  // Stav dovolené
  const [vacationDays, setVacationDays] = useState({ total: 20, used: 0, remaining: 20 });
  const [allUsers, setAllUsers] = useState([]);


  // Admin: poznámky a souhrny
  const [allAttendanceRecords, setAllAttendanceRecords] = useState([]);
  const [allHomeOfficeRecords, setAllHomeOfficeRecords] = useState([]);
  const [adminAttendanceAll, setAdminAttendanceAll] = useState([]);
  const [adminHomeOfficeAll, setAdminHomeOfficeAll] = useState([]);

  // UI: 
  const [noteModal, setNoteModal] = useState({ isOpen: false, recordId: null, collectionName: '', initialText: '' });
  const [profileModalOpen, setProfileModalOpen] = useState(false);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [activeView, setActiveView] = useState('dashboard'); // 'dashboard' | 'calendar' | 'admin'

  // Form stavy (dovolená/HO/doktor)
  const [leaveType, setLeaveType] = useState('full-day'); // 'full-day' | 'half-day' | 'hours'
  const [halfDayPart, setHalfDayPart] = useState('AM');
  const [hourLeaveStart, setHourLeaveStart] = useState('08:30');
  const [hourLeaveEnd, setHourLeaveEnd] = useState('17:00');
  const [leaveStartDate, setLeaveStartDate] = useState('');
  const [leaveEndDate, setLeaveEndDate] = useState('');

  const [homeOfficeStartDate, setHomeOfficeStartDate] = useState('');
  const [homeOfficeEndDate, setHomeOfficeEndDate] = useState('');

  const [doctorVisitDate, setDoctorVisitDate] = useState('');
  const [isFullDayDoctorVisit, setIsFullDayDoctorVisit] = useState(false);
  const [doctorVisitStartTime, setDoctorVisitStartTime] = useState('');
  const [doctorVisitEndTime, setDoctorVisitEndTime] = useState('');
  const [doctorVisitReason, setDoctorVisitReason] = useState('');

  // Editace a modifikace žádostí
  const [editModal, setEditModal] = useState({ isOpen: false, request: null });
  const [modificationModal, setModificationModal] = useState({ isOpen: false, request: null });
  const [modificationRequests, setModificationRequests] = useState([]);
  const [modificationReason, setModificationReason] = useState('');
  const [isRetroactive, setIsRetroactive] = useState(false);
  const [retroactiveReason, setRetroactiveReason] = useState('');

  // Admin: výběr měsíce a souhrn
  const now = new Date();
  const defaultMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  const [adminMonth, setAdminMonth] = useState(defaultMonth);
  const [adminSummaryRows, setAdminSummaryRows] = useState([]);

// ---------------------------
//     AUTENTIZACE
// ---------------------------

// 1) SSO pokus ještě před onAuthStateChanged
useEffect(() => {
  const attemptSsoFromBackoffice = async () => {
    try {
      if (auth.currentUser) return;

      // a) vezmi token z URL hash (při prvním příchodu z dashboardu)
      let tokenFromHash = null;
      if (window.location.hash && window.location.hash.length > 1) {
        const params = new URLSearchParams(window.location.hash.slice(1));
        tokenFromHash = params.get('id_token');
        if (tokenFromHash) {
          sessionStorage.setItem('backoffice_id_token', tokenFromHash);
          // vyčisti URL, ať tam token nezůstane
          window.history.replaceState(null, '', window.location.pathname + window.location.search);
        }
      }

      // b) nebo použij token už uložený na této (docházkové) doméně
      const idTokenFromDashboard =
        tokenFromHash || sessionStorage.getItem('backoffice_id_token');

      if (!idTokenFromDashboard) return;

      // c) zavolej Cloud Function exchangeToken v projektu docházky
      const res = await fetch(
        'https://us-central1-dochazkovy-system-prod.cloudfunctions.net/exchangeToken',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${idTokenFromDashboard}`,
          },
        }
      );
      if (!res.ok) throw new Error(`exchangeToken failed: ${res.status}`);

      const { customToken, profile } = await res.json();

      // d) přihlas Firebase Auth v projektu docházky
      await signInWithCustomToken(auth, customToken);

      // e) zapiš/načti profil a roli v kolekci 'zamestnanci'
      const userDocRef = doc(db, 'zamestnanci', profile.uid);
      const snap = await getDoc(userDocRef);
      if (snap.exists()) {
        setUserData(snap.data());
      } else {
        await setDoc(userDocRef, {
          name: profile.name || profile.email,
          email: profile.email,
          role: profile.role || 'employee',
          remaining_leave: 20, // Default
        });
        setUserData({
          name: profile.name || profile.email,
          email: profile.email,
          role: profile.role || 'employee',
          remaining_leave: 20,
        });
      }
    } catch (e) {
      console.error('SSO exchange error:', e);
    }
  };
  attemptSsoFromBackoffice();
}, []);


// 2) Standardní listener pro přihlášení
useEffect(() => {
  const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
    if (currentUser) {
      try {
        await enableNetwork(getFirestore());
        setUser(currentUser);
        const userDocRef = doc(db, 'zamestnanci', currentUser.uid);
        const snap = await getDoc(userDocRef);
        if (snap.exists()) {
          setUserData(snap.data());
        } else {
          const newUserData = {
            name: currentUser.displayName || currentUser.email,
            email: currentUser.email,
            role: 'employee',
            remaining_leave: 20, // Defaultní hodnota
          };
          await setDoc(userDocRef, newUserData);
          setUserData(newUserData);
        }
      } catch (e) {
        console.error('Chyba při načítání uživatele:', e);
        setError('Nepodařilo se načíst data uživatele z databáze.');
      }
    } else {
      setUser(null);
      setUserData(null);
    }
  });
  return () => unsubscribe();
}, []);

// 3) Ostatní efekty pro čas a stav
useEffect(() => {
  const id = setInterval(() => setCurrentTime(new Date()), 1000);
  return () => clearInterval(id);
}, []);

useEffect(() => {
  if (user === null || (user && userData)) setLoading(false);
}, [user, userData]);

useEffect(() => {
  if (userData?.role === 'admin') setActiveView('admin');
  else setActiveView('dashboard');
}, [userData]);


  const handleLogin = async (email, password) => {
    setError('');
    setLoading(true);
    try {
      await signInWithEmailAndPassword(auth, email, password);
    } catch (err) {
      console.error(err);
      setError('Nepodařilo se přihlásit.');
      setLoading(false);
    }
  };

  const handleLogout = () => {
    signOut(auth);
    setLeaveRequests([]);
    setDoctorVisits([]);
    setHomeOfficeRequests([]);
    setMyAttendance([]);
    setMyHomeOfficeRecords([]);
    setCurrentAttendanceStatus({ status: 'out', recordId: null });
    setCurrentHomeOfficeStatus({ status: 'finished', recordId: null });
    setIsHomeOfficeDay(false);
  };

  // ---------------------------
  //  POSLUCHAČE (Realtime Firestore)
  // ---------------------------
  useEffect(() => {
    if (!user || !userData) return;

    const setupRequestListeners = (collectionName, stateSetter) => {
      const unsubs = [];
      if (userData.role === 'admin') {
        const qAll = query(collection(db, collectionName), orderBy('requestedAt', 'desc'));
        unsubs.push(onSnapshot(qAll, (snapshot) => stateSetter(snapshot.docs.map((d) => ({ id: d.id, ...d.data() })))));
      } else {
        let approvedData = [];
        let myOtherData = [];
        const combine = () => {
          const combined = [...approvedData, ...myOtherData];
          const map = new Map();
          combined.forEach((it) => map.set(it.id, it));
          stateSetter(Array.from(map.values()).sort((a, b) => (b.requestedAt?.toMillis() || 0) - (a.requestedAt?.toMillis() || 0)));
        };
        unsubs.push(
          onSnapshot(query(collection(db, collectionName), where('status', '==', 'approved')), (snap) => {
            approvedData = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
            combine();
          })
        );
        unsubs.push(
          onSnapshot(query(collection(db, collectionName), where('userId', '==', user.uid), where('status', 'in', ['pending', 'rejected'])), (snap) => {
            myOtherData = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
            combine();
          })
        );
      }
      return unsubs;
    };

    // Moje docházka (office)
    const unsubAttendance = onSnapshot(query(collection(db, 'attendance_records'), where('userId', '==', user.uid)), (snapshot) => {
      const recs = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
      recs.sort((a, b) => (b.timestampIn?.toMillis() || 0) - (a.timestampIn?.toMillis() || 0));
      setMyAttendance(recs);
      const active = recs.find((r) => r.status === 'clocked-in');
      setCurrentAttendanceStatus(active ? { status: 'in', recordId: active.id } : { status: 'out', recordId: null });
    });

    // Moje HO
    const unsubHoRecords = onSnapshot(query(collection(db, 'home_office_records'), where('userId', '==', user.uid)), (snapshot) => {
      const recs = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
      recs.sort((a, b) => (b.timestampIn?.toMillis() || 0) - (a.timestampIn?.toMillis() || 0));
      setMyHomeOfficeRecords(recs);
      const active = recs.find((r) => r.status === 'working');
      setCurrentHomeOfficeStatus(active ? { status: 'working', recordId: active.id } : { status: 'finished', recordId: null });
    });

    // Žádosti
    const unsubs = [
      ...setupRequestListeners('leave_requests', setLeaveRequests),
      ...setupRequestListeners('doctor_visits', setDoctorVisits),
      ...setupRequestListeners('home_office_requests', setHomeOfficeRequests),
      unsubAttendance,
      unsubHoRecords,
    ];

    // Admin: kompletní záznamy pro souhrny + poznámky
    if (userData.role === 'admin') {
      unsubs.push(
        onSnapshot(collection(db, 'zamestnanci'), (snap) => {
          const usersWithoutAdmins = snap.docs.map(d => ({ id: d.id, ...d.data()})).filter(u => u.role !== 'admin');
          setAllUsers(usersWithoutAdmins);
        })
      );
      unsubs.push(
        onSnapshot(collection(db, 'attendance_records'), (snap) => {
          const all = snap.docs.map((d) => ({ ...d.data(), id: d.id, type: 'office' }));
          setAllAttendanceRecords(all.filter((r) => (r.dailyNote ?? '') !== ''));
          setAdminAttendanceAll(all);
        })
      );
      unsubs.push(
        onSnapshot(collection(db, 'home_office_records'), (snap) => {
          const all = snap.docs.map((d) => ({ ...d.data(), id: d.id, type: 'ho' }));
          setAllHomeOfficeRecords(all.filter((r) => (r.dailyNote ?? '') !== ''));
          setAdminHomeOfficeAll(all);
        })
      );
      // Modification requests pro admina (všechny)
      unsubs.push(
        onSnapshot(collection(db, 'modification_requests'), (snap) => {
          const requests = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
          // Seřadit podle requestedAt
          requests.sort((a, b) => (b.requestedAt?.toMillis?.() ?? 0) - (a.requestedAt?.toMillis?.() ?? 0));
          setModificationRequests(requests);
        })
      );
    } else {
      // Modification requests pro zaměstnance (pouze vlastní)
      unsubs.push(
        onSnapshot(query(collection(db, 'modification_requests'), where('userId', '==', user.uid)), (snap) => {
          setModificationRequests(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
        })
      );
    }

    return () => unsubs.forEach((u) => u && u());
  }, [user, userData]);

  // Je dnes schválený HO pro přihlášeného?
  useEffect(() => {
    if (!user || homeOfficeRequests.length === 0) {
      setIsHomeOfficeDay(false);
      return;
    }
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const approvedToday = homeOfficeRequests.some((req) => {
      if (req.userId === user.uid && req.status === 'approved') {
        const s = req.startDate.toDate();
        const e = req.endDate.toDate();
        s.setHours(0, 0, 0, 0);
        e.setHours(0, 0, 0, 0);
        return today >= s && today <= e;
      }
      return false;
    });
    setIsHomeOfficeDay(approvedToday);
  }, [user, homeOfficeRequests]);

  // VÝPOČET ZBÝVAJÍCÍ DOVOLENÉ PRO PŘIHLÁŠENÉHO UŽIVATELE
  useEffect(() => {
    if (!user || !userData) return;

    const currentYear = new Date().getFullYear();
    const usedHours = leaveRequests
      .filter(req => req.userId === user.uid && req.status === 'approved' && req.startDate.toDate().getFullYear() === currentYear)
      .reduce((acc, req) => acc + computeTotalLeaveHours(req), 0);
    
    const usedDays = usedHours / VACATION_HOURS_PER_WORKDAY;
    const totalDays = userData?.remaining_leave || 20;
    
    setVacationDays({
        total: totalDays,
        used: usedDays,
        remaining: totalDays - usedDays,
    });
  }, [user, userData, leaveRequests]);


  // ---------------------------
  //          AKCE
  // ---------------------------
  const createRecord = async (collectionName, status) => {
    if (!user || !userData) return;
    try {
      await addDoc(collection(db, collectionName), {
        userId: user.uid,
        userName: userData.name,
        timestampIn: serverTimestamp(),
        timestampOut: null,
        status,
        dailyNote: '',
      });
    } catch (err) {
      console.error(`Chyba při vytvoření záznamu v ${collectionName}:`, err);
      setError('Akce se nezdařila.');
    }
  };

  const updateRecord = async (collectionName, recordId, status) => {
    if (!user || !recordId) return;
    try {
      await updateDoc(doc(db, collectionName, recordId), { timestampOut: serverTimestamp(), status });
    } catch (err) {
      console.error(`Chyba při aktualizaci záznamu v ${collectionName}:`, err);
      setError('Akce se nezdařila.');
    }
  };

  const handleClockIn = () => createRecord('attendance_records', 'clocked-in');
  const handleClockOut = () => updateRecord('attendance_records', currentAttendanceStatus.recordId, 'clocked-out');
  const handleStartWorkHomeOffice = () => createRecord('home_office_records', 'working');
  const handleEndWorkHomeOffice = () => updateRecord('home_office_records', currentHomeOfficeStatus.recordId, 'finished');

  // Generická tvorba žádosti
  const createRequest = async (collectionName, data) => {
    if (!user || !userData) {
      setError('Nejste přihlášen.');
      return;
    }
    setError('');
    try {
      await addDoc(collection(db, collectionName), {
        userId: user.uid,
        userName: userData.name,
        requestedAt: serverTimestamp(),
        status: 'pending',
        approvedBy: null,
        ...data,
      });
    } catch (err) {
      console.error(`Chyba při odesílání žádosti do ${collectionName}:`, err);
      setError('Žádost se nepodařilo odeslat.');
    }
  };

  // Odeslání žádostí – dovolená
  const submitLeaveRequest = (e) => {
    e?.preventDefault?.();
    if (!leaveStartDate || !leaveEndDate) {
      setError('Vyplňte prosím všechna pole.');
      return;
    }
    const payload = {
      leaveType, // 'full-day' | 'half-day' | 'hours'
      halfDayPart: leaveType === 'half-day' ? halfDayPart : null,
      startDate: Timestamp.fromDate(new Date(leaveStartDate)),
      endDate: Timestamp.fromDate(new Date(leaveEndDate)),
      startTime: leaveType === 'hours' ? hourLeaveStart : null,
      endTime: leaveType === 'hours' ? hourLeaveEnd : null,
    };
    createRequest('leave_requests', payload);
    setLeaveStartDate('');
    setLeaveEndDate('');
  };

  // Odeslání žádosti – HO
  const submitHomeOfficeRequest = (e) => {
    e?.preventDefault?.();
    if (!homeOfficeStartDate || !homeOfficeEndDate) {
      setError('Vyplňte prosím všechna pole.');
      return;
    }
    if (isRetroactive && !retroactiveReason.trim()) {
      setError('Při zpětném hlášení vyplňte prosím důvod.');
      return;
    }
    createRequest('home_office_requests', {
      startDate: Timestamp.fromDate(new Date(homeOfficeStartDate)),
      endDate: Timestamp.fromDate(new Date(homeOfficeEndDate)),
      isRetroactive,
      retroactiveReason: isRetroactive ? retroactiveReason : null,
    });
    setHomeOfficeStartDate('');
    setHomeOfficeEndDate('');
    setIsRetroactive(false);
    setRetroactiveReason('');
  };

  // Odeslání žádosti – lékař
  const submitDoctorVisit = (e) => {
    e?.preventDefault?.();
    if (!doctorVisitDate || (!isFullDayDoctorVisit && (!doctorVisitStartTime || !doctorVisitEndTime))) {
      setError('Vyplňte prosím všechna povinná pole.');
      return;
    }
    createRequest('doctor_visits', {
      visitDate: Timestamp.fromDate(new Date(doctorVisitDate)),
      isFullDay: isFullDayDoctorVisit,
      visitStartTime: isFullDayDoctorVisit ? null : doctorVisitStartTime,
      visitEndTime: isFullDayDoctorVisit ? null : doctorVisitEndTime,
      reason: doctorVisitReason,
    });
    setDoctorVisitDate('');
    setIsFullDayDoctorVisit(false);
    setDoctorVisitStartTime('');
    setDoctorVisitEndTime('');
    setDoctorVisitReason('');
  };

  // Schvalování / zamítání žádostí (admin)
  const updateRequestStatus = async (collectionName, requestId, newStatus) => {
    if (!user || userData?.role !== 'admin') {
      setError('Nemáte oprávnění.');
      return;
    }
    try {
      await updateDoc(doc(db, collectionName, requestId), { status: newStatus, approvedBy: user.uid });
    } catch (err) {
      console.error('Chyba při aktualizaci žádosti:', err);
      setError('Stav žádosti se nepodařilo změnit.');
    }
  };

  // Editace pending žádosti
  const updatePendingRequest = async (collectionName, requestId, updatedData) => {
    if (!user) {
      setError('Nejste přihlášen.');
      return;
    }
    try {
      await updateDoc(doc(db, collectionName, requestId), {
        ...updatedData,
        modifiedAt: serverTimestamp(),
        modifiedBy: user.uid,
      });
      setEditModal({ isOpen: false, request: null });
      setError('');
    } catch (err) {
      console.error('Chyba při úpravě žádosti:', err);
      setError('Žádost se nepodařilo upravit.');
    }
  };

  // Vytvoření žádosti o změnu schválené žádosti
  const createModificationRequest = async (originalRequest, requestedChanges) => {
    if (!user || !userData) {
      setError('Nejste přihlášen.');
      return;
    }
    if (!modificationReason.trim()) {
      setError('Vyplňte prosím důvod změny.');
      return;
    }
    try {
      await addDoc(collection(db, 'modification_requests'), {
        userId: user.uid,
        userName: userData.name,
        originalRequestId: originalRequest.id,
        originalCollection: originalRequest.coll,
        requestedChanges,
        reason: modificationReason,
        requestedAt: serverTimestamp(),
        status: 'pending',
        approvedBy: null,
      });
      setModificationModal({ isOpen: false, request: null });
      setModificationReason('');
      setError('');
    } catch (err) {
      console.error('Chyba při vytváření žádosti o změnu:', err);
      setError('Žádost o změnu se nepodařilo odeslat.');
    }
  };

  // Schválení/zamítnutí žádosti o změnu (admin)
  const handleModificationRequest = async (modReqId, approve) => {
    if (!user || userData?.role !== 'admin') {
      setError('Nemáte oprávnění.');
      return;
    }
    try {
      const modReqRef = doc(db, 'modification_requests', modReqId);
      const modReqSnap = await getDoc(modReqRef);
      if (!modReqSnap.exists()) {
        setError('Žádost o změnu nenalezena.');
        return;
      }
      const modReqData = modReqSnap.data();

      if (approve) {
        // Schválit - aplikovat změny na původní žádost
        const originalRef = doc(db, modReqData.originalCollection, modReqData.originalRequestId);
        await updateDoc(originalRef, {
          ...modReqData.requestedChanges,
          modifiedAt: serverTimestamp(),
          modifiedBy: user.uid,
        });
      }

      // Označit modification request jako schválený/zamítnutý
      await updateDoc(modReqRef, {
        status: approve ? 'approved' : 'rejected',
        approvedBy: user.uid,
      });
    } catch (err) {
      console.error('Chyba při zpracování žádosti o změnu:', err);
      setError('Žádost o změnu se nepodařilo zpracovat.');
    }
  };

  // Uložení denní poznámky
  const saveNote = async (noteText) => {
    const { recordId, collectionName } = noteModal;
    if (!recordId || !collectionName) return;
    try {
      await updateDoc(doc(db, collectionName, recordId), { dailyNote: noteText });
    } catch (err) {
      console.error('Chyba při ukládání poznámky:', err);
      setError('Poznámku se nepodařilo uložit.');
    }
  };

  // ---------------------------
  //  VÝPOČET A EXPORT: ADMIN
  // ---------------------------
  useEffect(() => {
    if (userData?.role !== 'admin' || allUsers.length === 0) return;

    const { y, m } = parseMonthParts(adminMonth);
    const byUser = new Map();

    // 1. Inicializace všech uživatelů
    allUsers.forEach(u => {
        byUser.set(u.id, {
            name: u.name,
            officeHours: 0,
            hoHours: 0,
            leaveHours: 0, // pro daný měsíc
            totalVacationDays: u.remaining_leave || 20,
            usedVacationHours: 0, // za celý rok
        });
    });

    // 2. Zpracování docházky (kancelář + HO) pro daný měsíc
    const processRecords = (records, type) => {
        records.forEach((rec) => {
            if (!rec.timestampIn || !rec.timestampOut) return;
            const start = rec.timestampIn.toDate();
            const end = rec.timestampOut.toDate();
            const monthStart = new Date(y, m - 1, 1);
            const monthEnd = new Date(y, m, 0, 23, 59, 59, 999);
            const s = new Date(Math.max(start.getTime(), monthStart.getTime()));
            const e = new Date(Math.min(end.getTime(), monthEnd.getTime()));
            if (e < s) return;

            const hours = computeWorkedHoursSpan(s, e);
            const entry = byUser.get(rec.userId);
            if (entry) {
                if (type === 'office') entry.officeHours += hours;
                else entry.hoHours += hours;
            }
        });
    };
    processRecords(adminAttendanceAll, 'office');
    processRecords(adminHomeOfficeAll, 'ho');

    // 3. Zpracování dovolené pro daný měsíc
    leaveRequests.forEach((req) => {
        if (req.status !== 'approved') return;
        const hours = computeLeaveHoursInMonth(req, y, m);
        if (hours > 0) {
            const entry = byUser.get(req.userId);
            if(entry) entry.leaveHours += hours;
        }
    });
    
    // 4. Výpočet celkové vyčerpané dovolené za rok
    const currentYear = new Date().getFullYear();
    leaveRequests.forEach(req => {
        if (req.status === 'approved' && req.startDate.toDate().getFullYear() === currentYear) {
            const hours = computeTotalLeaveHours(req);
            const entry = byUser.get(req.userId);
            if (entry) {
                entry.usedVacationHours += hours;
            }
        }
    });

    // 5. Sestavení finálních řádků
    const rows = Array.from(byUser.entries()).map(([userId, v]) => {
        const usedVacationDays = v.usedVacationHours / VACATION_HOURS_PER_WORKDAY;
        const remainingDays = v.totalVacationDays - usedVacationDays;

        return {
            userId,
            name: v.name,
            officeHours: Math.round(v.officeHours * 10) / 10,
            hoHours: Math.round(v.hoHours * 10) / 10,
            leaveHours: Math.round(v.leaveHours * 10) / 10,
            remainingVacationDays: remainingDays,
            remainingVacationHours: remainingDays * VACATION_HOURS_PER_WORKDAY,
        };
    }).sort((a, b) => a.name.localeCompare(b.name, 'cs'));

    setAdminSummaryRows(rows);

}, [userData, adminMonth, adminAttendanceAll, adminHomeOfficeAll, leaveRequests, allUsers]);

  const exportMonthlyReport = () => {
    const headers = ['Zamestnanec', 'Hodiny_kancelar', 'Hodiny_HO', 'Hodiny_dovolena', 'Mesic'];
    const data = adminSummaryRows.map((r) => ({
      Zamestnanec: r.name,
      Hodiny_kancelar: r.officeHours,
      Hodiny_HO: r.hoHours,
      Hodiny_dovolena: r.leaveHours,
      Mesic: adminMonth,
    }));
    exportToCsv(data, `mesicni_vykaz_${adminMonth}.csv`, headers);
  };

  const exportMyHistory = () => {
    const data = [...myAttendance, ...myHomeOfficeRecords]
      .map((rec) => ({
        Typ: rec.status.includes('clock') ? 'V kanceláři' : 'Home Office',
        Datum: formatDateOnly(rec.timestampIn),
        Prichod: rec.timestampIn ? rec.timestampIn.toDate().toLocaleTimeString('cs-CZ') : '',
        Odchod: rec.timestampOut ? rec.timestampOut.toDate().toLocaleTimeString('cs-CZ') : '',
        Poznamka: rec.dailyNote || '',
      }))
      .sort((a, b) => new Date(b.Datum) - new Date(a.Datum));
    exportToCsv(data, 'moje_dochazka.csv', ['Typ', 'Datum', 'Prichod', 'Odchod', 'Poznamka']);
  };

  const exportAllNotes = () => {
    const data = [...allAttendanceRecords, ...allHomeOfficeRecords].map((note) => ({
      Datum: formatDateOnly(note.timestampIn),
      Zamestnanec: note.userName,
      Poznamka: note.dailyNote,
    }));
    exportToCsv(data, 'denni_poznamky.csv', ['Datum', 'Zamestnanec', 'Poznamka']);
  };

  // Mapa pending modification requests podle původní žádosti
  const pendingModificationMap = useMemo(() => {
    const map = new Map();
    modificationRequests
      .filter(modReq => modReq.status === 'pending')
      .forEach(modReq => {
        const key = `${modReq.originalCollection}-${modReq.originalRequestId}`;
        map.set(key, modReq);
      });
    return map;
  }, [modificationRequests]);

  // ---------------------------
  //   NAVRATOVÁ HODNOTA HOOKU
  // ---------------------------
  return {
    // stav
    user, userData, loading, error,
    leaveRequests, doctorVisits, homeOfficeRequests,
    myAttendance, myHomeOfficeRecords,
    currentAttendanceStatus, currentHomeOfficeStatus, isHomeOfficeDay,
    allAttendanceRecords, allHomeOfficeRecords,
    adminAttendanceAll, adminHomeOfficeAll,
    noteModal, setNoteModal,
    profileModalOpen, setProfileModalOpen,
    currentTime, activeView, setActiveView,
    vacationDays, // Nový stav pro zaměstnance

    // formy
    leaveType, setLeaveType,
    halfDayPart, setHalfDayPart,
    hourLeaveStart, setHourLeaveStart,
    hourLeaveEnd, setHourLeaveEnd,
    leaveStartDate, setLeaveStartDate,
    leaveEndDate, setLeaveEndDate,

    homeOfficeStartDate, setHomeOfficeStartDate,
    homeOfficeEndDate, setHomeOfficeEndDate,

    doctorVisitDate, setDoctorVisitDate,
    isFullDayDoctorVisit, setIsFullDayDoctorVisit,
    doctorVisitStartTime, setDoctorVisitStartTime,
    doctorVisitEndTime, setDoctorVisitEndTime,
    doctorVisitReason, setDoctorVisitReason,

    // Editace a modifikace
    editModal, setEditModal,
    modificationModal, setModificationModal,
    modificationRequests,
    pendingModificationMap,
    modificationReason, setModificationReason,
    isRetroactive, setIsRetroactive,
    retroactiveReason, setRetroactiveReason,

    adminMonth, setAdminMonth,
    adminSummaryRows,

    // akce
    handleLogin, handleLogout,
    handleClockIn, handleClockOut,
    handleStartWorkHomeOffice, handleEndWorkHomeOffice,

    submitLeaveRequest, submitHomeOfficeRequest, submitDoctorVisit,
    updateRequestStatus,
    updatePendingRequest,
    createModificationRequest,
    handleModificationRequest,

    saveNote,

    exportMonthlyReport, exportMyHistory, exportAllNotes,
  };
};
// =============================================
// App.js – Blok D (upraveno)
// UI: LAYOUT • PŘIHLÁŠENÍ • FORMULÁŘE • TABULKY • ADMIN PŘEHLEDY
// Napojené na hook useAppLogic() z Bloku C a UI komponenty z Bloku B
// =============================================

export default function App() {
  // Nastavení názvu stránky po načtení
  useEffect(() => {
    document.title = 'Docházkový systém';
  }, []);

  {
  const hash = typeof window !== 'undefined' ? window.location.hash : '';
  if (hash && hash.includes('id_token=')) {
    const m = hash.match(/id_token=([^&]+)/);
    if (m && m[1]) {
      try {
        const token = decodeURIComponent(m[1]);
        sessionStorage.setItem('backoffice_id_token', token);
      } catch {}
    }
    if (typeof window !== 'undefined' && window.history?.replaceState) {
      window.history.replaceState(null, '', window.location.pathname + window.location.search);
    }
  }
}

  const {


    // stav
    user, userData, loading, error,
    leaveRequests, doctorVisits, homeOfficeRequests,
    myAttendance, myHomeOfficeRecords,
    currentAttendanceStatus, currentHomeOfficeStatus, isHomeOfficeDay,
    noteModal, setNoteModal,
    profileModalOpen, setProfileModalOpen,
    currentTime, activeView, setActiveView,
    allAttendanceRecords, allHomeOfficeRecords,
    vacationDays,
    

    // formy
    leaveType, setLeaveType,
    halfDayPart, setHalfDayPart,
    hourLeaveStart, setHourLeaveStart,
    hourLeaveEnd, setHourLeaveEnd,
    leaveStartDate, setLeaveStartDate,
    leaveEndDate, setLeaveEndDate,

    homeOfficeStartDate, setHomeOfficeStartDate,
    homeOfficeEndDate, setHomeOfficeEndDate,

    doctorVisitDate, setDoctorVisitDate,
    isFullDayDoctorVisit, setIsFullDayDoctorVisit,
    doctorVisitStartTime, setDoctorVisitStartTime,
    doctorVisitEndTime, setDoctorVisitEndTime,
    doctorVisitReason, setDoctorVisitReason,

    // Editace a modifikace
    editModal, setEditModal,
    modificationModal, setModificationModal,
    modificationRequests,
    pendingModificationMap,
    modificationReason, setModificationReason,
    isRetroactive, setIsRetroactive,
    retroactiveReason, setRetroactiveReason,

    adminMonth, setAdminMonth,
    adminSummaryRows,

    // akce
    handleLogin, handleLogout,
    handleClockIn, handleClockOut,
    handleStartWorkHomeOffice, handleEndWorkHomeOffice,

    submitLeaveRequest, submitHomeOfficeRequest, submitDoctorVisit,
    updateRequestStatus,
    updatePendingRequest,
    createModificationRequest,
    handleModificationRequest,

    saveNote,

    exportMonthlyReport, exportMyHistory, exportAllNotes,
  } = useAppLogic();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  

// očekávané hodiny v měsíci (Po–Pá), 8 h/den (bez obědové pauzy, jako fond)
const workingDaysInMonth = (ym /* 'YYYY-MM' */) => {
  if (!ym || !/^\d{4}-\d{2}$/.test(ym)) return 0;
  const [y, m] = ym.split('-').map(Number);
  const first = new Date(y, m - 1, 1);
  const last = new Date(y, m, 0);
  let days = 0;
  for (let d = new Date(first); d <= last; d.setDate(d.getDate() + 1)) {
    const w = d.getDay();
    if (w !== 0 && w !== 6) days++; // Mon–Fri
  }
  return days;
};
const expectedHoursForMonth = (ym, hoursPerDay = 8) => workingDaysInMonth(ym) * hoursPerDay;

  // Sjednocené a seřazené žádosti pro admina i zaměstnance
  const combinedRequests = useMemo(() => {
    const toDate = (v) => (v && typeof v.toDate === 'function' ? v.toDate() : v ? new Date(v) : null);
    const fmt = (v, withTime = false) => {
      const d = toDate(v);
      if (!d || isNaN(d.getTime())) return 'N/A';
      return withTime ? d.toLocaleString('cs-CZ') : d.toLocaleDateString('cs-CZ');
    };
    const getSortDate = (req) => toDate(req.rawDate);

    const leaves = (leaveRequests || []).map((r) => ({ id: r.id, userId: r.userId, userName: r.userName, type: 'Dovolená', status: r.status, coll: 'leave_requests', period: r.leaveType === 'hours' ? `${fmt(r.startDate)} ${r.startTime}–${r.endTime}` : `${fmt(r.startDate)} – ${fmt(r.endDate)}`, subtype: r.leaveType === 'half-day' ? `Půl dne (${r.halfDayPart || ''})` : (r.leaveType === 'hours' ? 'Hodiny' : 'Celý den'), rawDate: r.startDate, endDate: r.endDate, raw: r }));
    const hos = (homeOfficeRequests || []).map((r) => ({ id: r.id, userId: r.userId, userName: r.userName, type: 'HO', status: r.status, coll: 'home_office_requests', period: `${fmt(r.startDate)} – ${fmt(r.endDate)}`, subtype: 'Home Office', rawDate: r.startDate, endDate: r.endDate, raw: r }));
    const doctors = (doctorVisits || []).map((r) => ({ id: r.id, userId: r.userId, userName: r.userName, type: 'Lékař', status: r.status, coll: 'doctor_visits', period: r.isFullDay ? `${fmt(r.visitDate)} (celodenní)` : `${fmt(r.visitDate)} ${r.visitStartTime || ''}–${r.visitEndTime || ''}`, subtype: r.isFullDay ? 'Celodenní návštěva' : 'Časová návštěva', rawDate: r.visitDate, endDate: r.visitDate, raw: r }));

    const combined = [...leaves, ...hos, ...doctors];

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // Zahrnout budoucí žádosti + žádosti s čekající modifikací (i z minulosti)
    const futureOrCurrentRequests = combined.filter(req => {
        const reqEndDate = toDate(req.endDate);
        if (!reqEndDate) return true;

        // Zahrnout žádosti, které ještě neskončily
        if (reqEndDate >= today) return true;

        // Zahrnout i minulé žádosti, pokud mají čekající modifikaci
        const modReqKey = `${req.coll}-${req.id}`;
        const hasPendingMod = pendingModificationMap.get(modReqKey);
        return hasPendingMod;
    });

    futureOrCurrentRequests.sort((a, b) => {
      const statusOrder = { 'pending': 1, 'approved': 2, 'rejected': 3 };
      const orderA = statusOrder[a.status] || 99;
      const orderB = statusOrder[b.status] || 99;
      if (orderA !== orderB) return orderA - orderB;
      const dateA = getSortDate(a);
      const dateB = getSortDate(b);
      if (!dateA || !dateB) return 0;
      return dateA - dateB;
    });

    return futureOrCurrentRequests;
  }, [leaveRequests, homeOfficeRequests, doctorVisits, pendingModificationMap]);

  // Filtrované žádosti jen pro přihlášeného zaměstnance
  const myCombinedRequests = useMemo(() => {
    if (!user) return [];
    return combinedRequests.filter(req => req.userId === user.uid);
  }, [combinedRequests, user]);

  // Pomocné formátování Timestamp/Date
  const fmtDate = (ts, withTime = false) => {
    if (!ts) return 'N/A';
    const d = typeof ts.toDate === 'function' ? ts.toDate() : new Date(ts);
    if (Number.isNaN(d.getTime())) return 'N/A';
    return withTime
      ? d.toLocaleString('cs-CZ')
      : d.toLocaleDateString('cs-CZ');
  };

  // Formátování požadovaných změn pro tooltip
  const formatModificationChanges = (changes, collectionType) => {
    if (!changes) return '';
    let text = '';

    if (collectionType === 'leave_requests') {
      if (changes.leaveType) {
        const typeMap = { 'full-day': 'Celý den', 'half-day': 'Půl dne', 'hours': 'Hodiny' };
        text += `Typ: ${typeMap[changes.leaveType] || changes.leaveType}\n`;
      }
      if (changes.halfDayPart) text += `Část dne: ${changes.halfDayPart === 'AM' ? 'Dopoledne' : 'Odpoledne'}\n`;
      if (changes.startDate) text += `Od: ${fmtDate(changes.startDate)}\n`;
      if (changes.endDate) text += `Do: ${fmtDate(changes.endDate)}\n`;
      if (changes.startTime) text += `Začátek: ${changes.startTime}\n`;
      if (changes.endTime) text += `Konec: ${changes.endTime}\n`;
    } else if (collectionType === 'home_office_requests') {
      if (changes.startDate) text += `Od: ${fmtDate(changes.startDate)}\n`;
      if (changes.endDate) text += `Do: ${fmtDate(changes.endDate)}\n`;
    } else if (collectionType === 'doctor_visits') {
      if (changes.visitDate) text += `Datum: ${fmtDate(changes.visitDate)}\n`;
      if (changes.isFullDay !== undefined) text += `Celodenní: ${changes.isFullDay ? 'Ano' : 'Ne'}\n`;
      if (changes.visitStartTime) text += `Od: ${changes.visitStartTime}\n`;
      if (changes.visitEndTime) text += `Do: ${changes.visitEndTime}\n`;
      if (changes.reason) text += `Důvod: ${changes.reason}\n`;
    }

    return text.trim();
  };

  // Sloučené „moje“ záznamy (kancelář + HO) pro přehled na dashboardu zaměstnance
  const myRecords = useMemo(() => {
    const office = (myAttendance || []).map(r => ({ ...r, _kind: 'Kancelář', _coll: 'attendance_records' }));
    const ho = (myHomeOfficeRecords || []).map(r => ({ ...r, _kind: 'Home Office', _coll: 'home_office_records' }));
    return [...office, ...ho].sort(
      (a, b) => (b.timestampIn?.toMillis?.() || 0) - (a.timestampIn?.toMillis?.() || 0)
    );
  }, [myAttendance, myHomeOfficeRecords]);

  
  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-100">
        <div className="text-xl font-semibold">Načítání...</div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
        <div className="sm:mx-auto sm:w-full sm:max-w-md">
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">Přihlášení do systému</h2>
        </div>
        <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
          <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
            <form className="space-y-6" onSubmit={(e)=>{e.preventDefault(); handleLogin(email, password);}}>
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700">Email</label>
                <input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500" />
              </div>
              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700">Heslo</label>
                <input id="password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500" />
              </div>
              {error && <p className="text-red-500 text-sm">{error}</p>}
              <div>
                <button type="submit" className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700">Přihlásit se</button>
              </div>
            </form>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="min-h-screen bg-gray-100">
        <header className="bg-white shadow-sm">
          <div className="max-w-7xl mx-auto py-4 px-4 sm:px-6 lg:px-8 flex justify-between items-center">
            <h1 className="text-2xl font-bold text-gray-900">Docházkový systém</h1>
            <div className="flex items-center space-x-4">
              <div className="flex items-center">
                <span className="ml-2 font-medium">{userData?.name} </span>
                <button onClick={() => setProfileModalOpen(true)} className="ml-3 p-1 rounded-full hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" title="Profil a změna hesla">
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
                </button>
              </div>
              <button onClick={handleLogout} className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-indigo-700 bg-indigo-100 hover:bg-indigo-200">
                <LogOutIcon />
                <span className="ml-2">Odhlásit</span>
              </button>
            </div>
          </div>
        </header>

        <main className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
          {error && (
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mb-4" role="alert">
              {error}
            </div>
          )}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* LEVÝ SLOUPEC – pro admina se nezobrazuje */}
            {userData?.role !== 'admin' && (
              <div className="lg:col-span-1 space-y-6">
                <div className="bg-white shadow rounded-lg p-6">
                  <h3 className="text-lg font-medium text-gray-900 flex items-center">
                    {isHomeOfficeDay ? (
                      <>
                        <HomeIcon />
                        <span className="ml-2">Home Office</span>
                      </>
                    ) : (
                      <>
                      </>
                    )}
                  </h3>
                  <div className="mt-2 text-center">
                    <p className="text-lg font-semibold text-gray-700 capitalize">
                      {new Intl.DateTimeFormat('cs-CZ', { weekday: 'long' }).format(currentTime)}
                    </p>
                    <p className="text-sm text-gray-500">
                      {new Intl.DateTimeFormat('cs-CZ', { day: '2-digit', month: '2-digit', year: 'numeric' }).format(currentTime)}
                    </p>
                    <p className="text-3xl font-bold text-gray-800 tracking-wider mt-1">
                      {new Intl.DateTimeFormat('cs-CZ', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false }).format(currentTime)}
                    </p>
                  </div>
                  {isHomeOfficeDay ? (
                    <>
                      <div className="mt-4">
                        <p className="text-sm text-gray-500 text-center">Stav:</p>
                        <p className={`text-2xl font-bold text-center ${currentHomeOfficeStatus.status === 'working' ? 'text-blue-600' : 'text-gray-500'}`}>
                          {currentHomeOfficeStatus.status === 'working' ? 'Pracujete' : 'Mimo pracovní dobu'}
                        </p>
                      </div>
                      <div className="mt-6 flex space-x-4">
                        <button onClick={handleStartWorkHomeOffice} disabled={currentHomeOfficeStatus.status === 'working'} className="w-full flex justify-center items-center py-2 px-4 border rounded-md text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300">
                          <LogInIcon /> <span className="ml-2">Zahájit práci</span>
                        </button>
                        <button onClick={handleEndWorkHomeOffice} disabled={currentHomeOfficeStatus.status !== 'working'} className="w-full flex justify-center items-center py-2 px-4 border rounded-md text-sm font-medium text-white bg-gray-600 hover:bg-gray-700 disabled:bg-gray-300">
                          <LogOutIcon /> <span className="ml-2">Ukončit práci</span>
                        </button>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="mt-4">
                        <p className="text-sm text-gray-500 text-center">Stav:</p>
                        <p className={`text-2xl font-bold text-center ${currentAttendanceStatus.status === 'in' ? 'text-green-600' : 'text-red-600'}`}>
                          {currentAttendanceStatus.status === 'in' ? 'V práci' : 'Mimo pracoviště'}
                        </p>
                      </div>
                      <div className="mt-6 flex space-x-4">
                        <button onClick={handleClockIn} disabled={currentAttendanceStatus.status === 'in'} className="w-full flex justify-center items-center py-2 px-4 border rounded-md text-sm font-medium text-white bg-green-600 hover:bg-green-700 disabled:bg-gray-300">
                          <LogInIcon /> <span className="ml-2">Příchod</span>
                        </button>
                        <button onClick={handleClockOut} disabled={currentAttendanceStatus.status === 'out'} className="w-full flex justify-center items-center py-2 px-4 border rounded-md text-sm font-medium text-white bg-red-600 hover:bg-red-700 disabled:bg-gray-300">
                          <LogOutIcon /> <span className="ml-2">Odchod</span>
                        </button>
                      </div>
                    </>
                  )}
                </div>

                <WorkFundVisualizer officeRecords={myAttendance} homeOfficeRecords={myHomeOfficeRecords} />
                
                {/* Přehled dovolené pro zaměstnance */}
                <div className="bg-white shadow rounded-lg p-6">
                  <h3 className="text-lg font-medium text-gray-900 flex items-center">
                    <PieChartIcon />
                    <span className="ml-2">Stav dovolené</span>
                  </h3>
                  <div className="mt-4 grid grid-cols-3 gap-4 text-center">
                      <div>
                          <p className="text-sm text-gray-500">Celkem</p>
                          <p className="text-2xl font-bold text-gray-800">{vacationDays.total.toFixed(1)}</p>
                          <p className="text-xs text-gray-500">dnů</p>
                      </div>
                      <div>
                          <p className="text-sm text-gray-500">Vyčerpáno</p>
                          <p className="text-2xl font-bold text-gray-800">{vacationDays.used.toFixed(1)}</p>
                          <p className="text-xs text-gray-500">dnů</p>
                      </div>
                      <div>
                          <p className="text-sm font-medium text-indigo-600">Zbývá</p>
                          <p className="text-2xl font-bold text-indigo-600">{vacationDays.remaining.toFixed(1)}</p>
                          <p className="text-xs text-indigo-500">dnů ({(vacationDays.remaining * VACATION_HOURS_PER_WORKDAY).toFixed(1)} h)</p>
                      </div>
                  </div>
                </div>


                {/* Žádost o dovolenou */}
                <div className="bg-white shadow rounded-lg p-6">
                  <h3 className="text-lg font-medium text-gray-900 flex items-center">
                    <CalendarIcon />
                    <span className="ml-2">Požádat o dovolenou</span>
                  </h3>
                  <form onSubmit={submitLeaveRequest} className="mt-4 space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Typ dovolené</label>
                      <div className="mt-1 grid grid-cols-3 gap-2">
                        <label className="inline-flex items-center space-x-2 p-2 border rounded">
                          <input type="radio" name="lt" value="full-day" checked={leaveType === 'full-day'} onChange={() => setLeaveType('full-day')} />
                          <span>Celý den</span>
                        </label>
                        <label className="inline-flex items-center space-x-2 p-2 border rounded">
                          <input type="radio" name="lt" value="half-day" checked={leaveType === 'half-day'} onChange={() => setLeaveType('half-day')} />
                          <span>Půl dne</span>
                        </label>
                        <label className="inline-flex items-center space-x-2 p-2 border rounded">
                          <input type="radio" name="lt" value="hours" checked={leaveType === 'hours'} onChange={() => setLeaveType('hours')} />
                          <span>Hodiny</span>
                        </label>
                      </div>
                    </div>

                    {leaveType === 'half-day' && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Část dne</label>
                        <div className="mt-1 grid grid-cols-2 gap-2">
                          <label className="inline-flex items-center space-x-2 p-2 border rounded">
                            <input type="radio" name="hd" value="AM" checked={halfDayPart === 'AM'} onChange={() => setHalfDayPart('AM')} />
                            <span>Dopoledne</span>
                          </label>
                          <label className="inline-flex items-center space-x-2 p-2 border rounded">
                            <input type="radio" name="hd" value="PM" checked={halfDayPart === 'PM'} onChange={() => setHalfDayPart('PM')} />
                            <span>Odpoledne</span>
                          </label>
                        </div>
                      </div>
                    )}

                    {leaveType === 'hours' && (
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Od</label>
                          <input type="time" value={hourLeaveStart} onChange={(e) => setHourLeaveStart(e.target.value)} className="mt-1 block w-full border-gray-300 rounded-md shadow-sm py-2 px-3" />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Do</label>
                          <input type="time" value={hourLeaveEnd} onChange={(e) => setHourLeaveEnd(e.target.value)} className="mt-1 block w-full border-gray-300 rounded-md shadow-sm py-2 px-3" />
                        </div>
                      </div>
                    )}

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label htmlFor="start-date" className="block text-sm font-medium text-gray-700">Od</label>
                        <input type="date" id="start-date" value={leaveStartDate} onChange={(e) => setLeaveStartDate(e.target.value)} required className="mt-1 block w-full border-gray-300 rounded-md shadow-sm py-2 px-3" />
                      </div>
                      <div>
                        <label htmlFor="end-date" className="block text-sm font-medium text-gray-700">Do</label>
                        <input type="date" id="end-date" value={leaveEndDate} onChange={(e) => setLeaveEndDate(e.target.value)} required className="mt-1 block w-full border-gray-300 rounded-md shadow-sm py-2 px-3" />
                      </div>
                    </div>

                    <button type="submit" className="w-full flex justify-center py-2 px-4 border rounded-md text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700">
                      Odeslat žádost
                    </button>
                  </form>
                </div>

                {/* Žádost o HO */}
                <div className="bg-white shadow rounded-lg p-6">
                  <h3 className="text-lg font-medium text-gray-900 flex items-center">
                    <HomeIcon />
                    <span className="ml-2">Požádat o Home Office</span>
                  </h3>
                  <form onSubmit={submitHomeOfficeRequest} className="mt-4 space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label htmlFor="ho-start-date" className="block text-sm font-medium text-gray-700">Od</label>
                        <input type="date" id="ho-start-date" value={homeOfficeStartDate} onChange={(e) => setHomeOfficeStartDate(e.target.value)} required className="mt-1 block w-full border-gray-300 rounded-md shadow-sm py-2 px-3" />
                      </div>
                      <div>
                        <label htmlFor="ho-end-date" className="block text sm font-medium text-gray-700">Do</label>
                        <input type="date" id="ho-end-date" value={homeOfficeEndDate} onChange={(e) => setHomeOfficeEndDate(e.target.value)} required className="mt-1 block w-full border-gray-300 rounded-md shadow-sm py-2 px-3" />
                      </div>
                    </div>
                    <div className="flex items-center">
                      <input id="is-retroactive" type="checkbox" checked={isRetroactive} onChange={(e) => setIsRetroactive(e.target.checked)} className="h-4 w-4 text-indigo-600 border-gray-300 rounded" />
                      <label htmlFor="is-retroactive" className="ml-2 block text-sm text-gray-900">Zpětné hlášení (již proběhlý home office)</label>
                    </div>
                    {isRetroactive && (
                      <div>
                        <label htmlFor="retroactive-reason" className="block text-sm font-medium text-gray-700">Důvod zpětného hlášení</label>
                        <textarea id="retroactive-reason" value={retroactiveReason} onChange={(e) => setRetroactiveReason(e.target.value)} required={isRetroactive} rows={3} className="mt-1 block w-full border-gray-300 rounded-md shadow-sm py-2 px-3" placeholder="Vysvětlete prosím, proč nebyla žádost podána včas..." />
                      </div>
                    )}
                    <button type="submit" className="w-full flex justify-center py-2 px-4 border rounded-md text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700">
                      Odeslat žádost
                    </button>
                  </form>
                </div>

                {/* Doktor */}
                <div className="bg-white shadow rounded-lg p-6">
                  <h3 className="text-lg font-medium text-gray-900 flex items-center">
                    <MedicalIcon />
                    <span className="ml-2">Nahlásit návštěvu lékaře</span>
                  </h3>
                  <form onSubmit={submitDoctorVisit} className="mt-4 space-y-4">
                    <div>
                      <label htmlFor="doctor-visit-date" className="block text-sm font-medium text-gray-700">Datum</label>
                      <input type="date" id="doctor-visit-date" value={doctorVisitDate} onChange={(e) => setDoctorVisitDate(e.target.value)} required className="mt-1 block w-full border-gray-300 rounded-md shadow-sm py-2 px-3" />
                    </div>
                    <div className="flex items-center">
                      <input id="is-full-day" type="checkbox" checked={isFullDayDoctorVisit} onChange={(e) => setIsFullDayDoctorVisit(e.target.checked)} className="h-4 w-4 text-indigo-600 border-gray-300 rounded" />
                      <label htmlFor="is-full-day" className="ml-2 block text-sm text-gray-900">Celodenní</label>
                    </div>
                    {!isFullDayDoctorVisit && (
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label htmlFor="doctor-visit-start-time" className="block text-sm font-medium text-gray-700">Čas od</label>
                          <input type="time" id="doctor-visit-start-time" value={doctorVisitStartTime} onChange={(e) => setDoctorVisitStartTime(e.target.value)} required={!isFullDayDoctorVisit} className="mt-1 block w-full border-gray-300 rounded-md shadow-sm py-2 px-3" />
                        </div>
                        <div>
                          <label htmlFor="doctor-visit-end-time" className="block text-sm font-medium text-gray-700">Čas do</label>
                          <input type="time" id="doctor-visit-end-time" value={doctorVisitEndTime} onChange={(e) => setDoctorVisitEndTime(e.target.value)} required={!isFullDayDoctorVisit} className="mt-1 block w-full border-gray-300 rounded-md shadow-sm py-2 px-3" />
                        </div>
                      </div>
                    )}
                    <div>
                      <label htmlFor="doctor-reason" className="block text-sm font-medium text-gray-700">Důvod</label>
                      <textarea id="doctor-reason" rows={2} value={doctorVisitReason} onChange={(e) => setDoctorVisitReason(e.target.value)} className="mt-1 block w-full border-gray-300 rounded-md shadow-sm py-2 px-3"></textarea>
                    </div>
                    <button type="submit" className="w-full flex justify-center py-2 px-4 border rounded-md text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700">
                      Odeslat žádost
                    </button>
                  </form>
                </div>
              </div>
            )}

            {/* PRAVÝ SLOUPEC */}
            <div className={userData?.role !== 'admin' ? 'lg:col-span-2 space-y-6' : 'lg:col-span-3 space-y-6'}>
              <div className="border-b border-gray-200">
                <div className="border-b border-gray-200">
                  <nav className="-mb-px flex space-x-8" aria-label="Tabs">
                    {userData?.role !== 'admin' && (
                      <button
                        onClick={() => setActiveView('dashboard')}
                        className={`${activeView === 'dashboard' ? 'border-indigo-500 text-indigo-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'} whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}>
                        Přehledy
                      </button>
                    )}
                    <button
                      onClick={() => setActiveView('calendar')}
                      className={`${activeView === 'calendar' ? 'border-indigo-500 text-indigo-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'} whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}>
                      Kalendář týmu
                    </button>
                    {userData?.role === 'admin' && (
                      <button
                        onClick={() => setActiveView('admin')}
                        className={`${activeView === 'admin' ? 'border-indigo-500 text-indigo-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'} whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}>
                        Admin přehledy
                      </button>
                    )}
                  </nav>
                </div>
              </div>

              {/* DASHBOARD ZAMĚSTNANCE */}
              {activeView === 'dashboard' && userData?.role !== 'admin' && (
                <div className="space-y-6">
                  <div className="bg-white shadow rounded-lg p-6">
                    <div className="flex items-center justify-between">
                      <h3 className="text-lg font-medium text-gray-900">Moje docházka</h3>
                      <button
                        onClick={exportMyHistory}
                        className="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-indigo-700 bg-indigo-100 hover:bg-indigo-200"
                      >
                        <DownloadIcon />
                        <span className="ml-2">Exportovat CSV</span>
                      </button>
                    </div>

                    {(() => {
                        const toTime = (ts) => (ts ? ts.toDate().toLocaleTimeString('cs-CZ') : '—');
                        return (
                          <div className="mt-4 overflow-x-auto">
                            <table className="min-w-full table-auto divide-y divide-gray-200">
                              <thead className="bg-gray-50">
                                <tr>
                                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Datum</th>
                                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Typ</th>
                                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Příchod</th>
                                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Odchod</th>
                                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Poznámka</th>
                                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Akce</th>
                                </tr>
                              </thead>
                              <tbody className="bg-white divide-y divide-gray-200">
                                {myRecords.map((rec) => (
                                  <tr key={`${rec._coll}-${rec.id}`}>
                                    <td className="px-6 py-4 text-sm text-gray-700 whitespace-nowrap">{fmtDate(rec.timestampIn)}</td>
                                    <td className="px-6 py-4 text-sm text-gray-700">{rec._kind}</td>
                                    <td className="px-6 py-4 text-sm text-gray-700">{toTime(rec.timestampIn)}</td>
                                    <td className="px-6 py-4 text-sm text-gray-700">{toTime(rec.timestampOut)}</td>
                                    <td className="px-6 py-4 text-sm text-gray-700 max-w-xs truncate" title={rec.dailyNote}>{rec.dailyNote || '—'}</td>
                                    <td className="px-6 py-4 text-sm font-medium">
                                      <button
                                        onClick={() => setNoteModal({ isOpen: true, recordId: rec.id, collectionName: rec._coll, initialText: rec.dailyNote || '' })}
                                        className="text-indigo-600 hover:text-indigo-900">
                                        Upravit
                                      </button>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        );
                    })()}
                  </div>

                  <div className="bg-white shadow rounded-lg p-6">
                      <h3 className="text-lg font-medium text-gray-900">Přehled žádostí</h3>
                      <div className="mt-4 overflow-x-auto">
                          <table className="min-w-full divide-y divide-gray-200">
                          <thead className="bg-gray-50">
                              <tr>
                                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Typ</th>
                                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Detail</th>
                                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Období / Čas</th>
                                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Stav</th>
                                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Akce</th>
                              </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200">
                              {myCombinedRequests.map((req) => {
                                const modReqKey = `${req.coll}-${req.id}`;
                                const pendingMod = pendingModificationMap.get(modReqKey);
                                return (
                                <tr key={modReqKey}>
                                  <td className="px-6 py-4 text-sm font-medium text-gray-900">{req.type}</td>
                                  <td className="px-6 py-4 text-sm text-gray-500">{req.subtype}</td>
                                  <td className="px-6 py-4 text-sm text-gray-500 whitespace-nowrap">{req.period}</td>
                                  <td className="px-6 py-4 text-sm text-gray-500">
                                    <div className="flex flex-col gap-1">
                                      <StatusBadge status={req.status} />
                                      {pendingMod && (
                                        <div
                                          className="text-xs text-orange-600 cursor-help inline-block"
                                          title={`Důvod: ${pendingMod.reason}\n\nPožadované změny:\n${formatModificationChanges(pendingMod.requestedChanges, req.coll)}`}
                                        >
                                          📝 Žádost o změnu odeslána
                                        </div>
                                      )}
                                    </div>
                                  </td>
                                  <td className="px-6 py-4 text-sm font-medium">
                                    {req.status === 'pending' && !pendingMod && (
                                      <button onClick={() => setEditModal({ isOpen: true, request: req })} className="text-indigo-600 hover:text-indigo-900">
                                        Upravit
                                      </button>
                                    )}
                                    {req.status === 'approved' && !pendingMod && (
                                      <button onClick={() => setModificationModal({ isOpen: true, request: req })} className="text-blue-600 hover:text-blue-900">
                                        Požádat o změnu
                                      </button>
                                    )}
                                    {pendingMod && (
                                      <span className="text-gray-400 text-xs">Čeká na schválení</span>
                                    )}
                                  </td>
                                </tr>
                              );
                              })}
                          </tbody>
                          </table>
                      </div>
                  </div>

                  {/* Moje žádosti o změnu */}
                  {modificationRequests.length > 0 && (
                    <div className="bg-white shadow rounded-lg p-6">
                      <h3 className="text-lg font-medium text-gray-900">Moje žádosti o změnu schválených žádostí</h3>
                      <div className="mt-4 overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Typ žádosti</th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Důvod změny</th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Požadováno</th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Stav</th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200">
                            {modificationRequests.map((modReq) => {
                              const collectionMap = {
                                'leave_requests': 'Dovolená',
                                'home_office_requests': 'Home Office',
                                'doctor_visits': 'Lékař'
                              };
                              return (
                                <tr key={modReq.id}>
                                  <td className="px-6 py-4 text-sm font-medium text-gray-900">
                                    {collectionMap[modReq.originalCollection] || modReq.originalCollection}
                                  </td>
                                  <td className="px-6 py-4 text-sm text-gray-700 max-w-xs">{modReq.reason}</td>
                                  <td className="px-6 py-4 text-sm text-gray-500">
                                    {modReq.requestedAt?.toDate ? modReq.requestedAt.toDate().toLocaleDateString('cs-CZ') : 'N/A'}
                                  </td>
                                  <td className="px-6 py-4 text-sm text-gray-500"><StatusBadge status={modReq.status} /></td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* KALENDÁŘ TÝMU */}
              {activeView === 'calendar' && (
                <TeamCalendar
                  leave={leaveRequests.filter((r) => r.status === 'approved')}
                  homeOffice={homeOfficeRequests.filter((r) => r.status === 'approved')}
                  doctor={doctorVisits.filter((r) => r.status === 'approved')}
                />
              )}

              {/* ADMIN PŘEHLEDY */}
              {activeView === 'admin' && userData?.role === 'admin' && (
                <div className="space-y-6">
                  {/* Souhrn měsíčních hodin */}
                  <div className="bg-white shadow rounded-lg p-6">
                    <div className="flex items-center justify-between">
                      <h3 className="text-lg font-medium text-gray-900">Admin přehledy – měsíční souhrn</h3>
                      <div className="flex items-center space-x-3">
                        <input type="month" value={adminMonth} onChange={(e) => setAdminMonth(e.target.value)} className="border border-gray-300 rounded-md px-3 py-2" />
                        <button onClick={exportMonthlyReport} className="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-indigo-700 bg-indigo-100 hover:bg-indigo-200">
                          <DownloadIcon />
                          <span className="ml-2">Exportovat CSV</span>
                        </button>
                      </div>
                    </div>

                    <div className="text-sm text-gray-600 mt-2">
                      Očekávaná pracovní doba v měsíci: <span className="font-medium">{expectedHoursForMonth(adminMonth).toFixed(1)} h</span>
                    </div>

                    <div className="mt-4 overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Zaměstnanec</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Hodiny v kanceláři</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Hodiny na HO</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Hodiny dovolené</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Zbývající dovolená</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Plnění pracovní doby</th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {adminSummaryRows.map((row) => {
                            const expected = expectedHoursForMonth(adminMonth);
                            const actual = (row.officeHours || 0) + (row.hoHours || 0) + (row.leaveHours || 0);
                            const pct = expected > 0 ? Math.round((actual / expected) * 100) : 0;
                            return (
                              <tr key={row.userId}>
                                <td className="px-6 py-4 text-sm font-medium text-gray-900">{row.name}</td>
                                <td className="px-6 py-4 text-sm text-gray-700">{row.officeHours.toFixed(1)}</td>
                                <td className="px-6 py-4 text-sm text-gray-700">{row.hoHours.toFixed(1)}</td>
                                <td className="px-6 py-4 text-sm text-gray-700">{row.leaveHours.toFixed(1)}</td>
                                <td className="px-6 py-4 text-sm text-gray-700 whitespace-nowrap">
                                  {row.remainingVacationDays.toFixed(1)} dnů / {(row.remainingVacationHours).toFixed(1)} h
                                </td>
                                <td className="px-6 py-4 text-sm text-gray-700">
                                  <div className="flex items-center space-x-2">
                                    <span className="font-medium">{pct}%</span>
                                    <div className="w-28 h-2 bg-gray-200 rounded">
                                      <div className="h-2 bg-indigo-500 rounded" style={{ width: `${Math.max(0, Math.min(100, pct))}%` }} />
                                    </div>
                                  </div>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {/* Žádosti zaměstnanců */}
                  <div className="bg-white shadow rounded-lg p-6">
                    <h3 className="text-lg font-medium text-gray-900">Žádosti zaměstnanců</h3>
                    <div className="mt-4 overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Typ</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Zaměstnanec</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Detail</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Období / Čas</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Stav</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Akce</th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {combinedRequests.map((req) => {
                            const modReqKey = `${req.coll}-${req.id}`;
                            const pendingMod = pendingModificationMap.get(modReqKey);
                            return (
                              <tr key={modReqKey}>
                                <td className="px-6 py-4 text-sm font-medium text-gray-900">{req.type}</td>
                                <td className="px-6 py-4 text-sm text-gray-700">{req.userName}</td>
                                <td className="px-6 py-4 text-sm text-gray-500">{req.subtype}</td>
                                <td className="px-6 py-4 text-sm text-gray-500 whitespace-nowrap">{req.period}</td>
                                <td className="px-6 py-4 text-sm text-gray-500">
                                  <div className="flex flex-col gap-1">
                                    <StatusBadge status={req.status} />
                                    {pendingMod && (
                                      <div
                                        className="text-xs text-orange-600 cursor-help inline-block"
                                        title={`Důvod: ${pendingMod.reason}\n\nPožadované změny:\n${formatModificationChanges(pendingMod.requestedChanges, req.coll)}`}
                                      >
                                        📝 Žádost o změnu odeslána
                                      </div>
                                    )}
                                  </div>
                                </td>
                                <td className="px-6 py-4 text-sm font-medium">
                                  {req.status === 'pending' && !pendingMod && (
                                    <div className="flex space-x-2">
                                      <button onClick={() => updateRequestStatus(req.coll, req.id, 'approved')} className="text-green-600 hover:text-green-900">Schválit</button>
                                      <button onClick={() => updateRequestStatus(req.coll, req.id, 'rejected')} className="text-red-600 hover:text-red-900">Zamítnout</button>
                                    </div>
                                  )}
                                  {pendingMod && (
                                    <div className="flex flex-col gap-2">
                                      <div className="text-xs font-semibold text-gray-700 mb-1">Žádost o změnu:</div>
                                      <div className="flex space-x-2">
                                        <button onClick={() => handleModificationRequest(pendingMod.id, true)} className="text-green-600 hover:text-green-900 text-sm">
                                          ✓ Schválit změnu
                                        </button>
                                        <button onClick={() => handleModificationRequest(pendingMod.id, false)} className="text-red-600 hover:text-red-900 text-sm">
                                          ✗ Zamítnout změnu
                                        </button>
                                      </div>
                                    </div>
                                  )}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {/* Denní poznámky */}
                  <div className="bg-white shadow rounded-lg p-6">
                    <div className="flex items-center justify-between">
                      <h3 className="text-lg font-medium text-gray-900">Denní poznámky</h3>
                      <button onClick={exportAllNotes} className="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-indigo-700 bg-indigo-100 hover:bg-indigo-200">
                        <DownloadIcon />
                        <span className="ml-2">Exportovat CSV</span>
                      </button>
                    </div>
                    <div className="mt-4 overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Datum</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Zaměstnanec</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Poznámka</th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {[...(allAttendanceRecords || []), ...(allHomeOfficeRecords || [])]
                            .filter((r) => (r.dailyNote ?? '').trim() !== '')
                            .sort((a, b) => (b.timestampIn?.toMillis?.() ?? 0) - (a.timestampIn?.toMillis?.() ?? 0))
                            .map((note) => (
                              <tr key={`${note.type || 'rec'}-${note.id}`}>
                                <td className="px-6 py-4 text-sm text-gray-500">{fmtDate(note.timestampIn, true)}</td>
                                <td className="px-6 py-4 text-sm font-medium text-gray-900">{note.userName}</td>
                                <td className="px-6 py-4 text-sm text-gray-700">{note.dailyNote}</td>
                              </tr>
                            ))}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {/* Žádosti o změnu */}
                  <div className="bg-white shadow rounded-lg p-6">
                    <h3 className="text-lg font-medium text-gray-900">Žádosti o změnu schválených žádostí</h3>
                    <div className="mt-4 overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Zaměstnanec</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Původní žádost</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Důvod změny</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Požadované změny</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Stav</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Akce</th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {modificationRequests.map((modReq) => {
                            const collectionMap = {
                              'leave_requests': 'Dovolená',
                              'home_office_requests': 'Home Office',
                              'doctor_visits': 'Lékař'
                            };
                            const changesText = formatModificationChanges(modReq.requestedChanges, modReq.originalCollection);
                            return (
                              <tr key={modReq.id}>
                                <td className="px-6 py-4 text-sm font-medium text-gray-900">{modReq.userName}</td>
                                <td className="px-6 py-4 text-sm text-gray-500">
                                  {collectionMap[modReq.originalCollection] || modReq.originalCollection}
                                  <br />
                                  <span className="text-xs text-gray-400">ID: {modReq.originalRequestId.substring(0, 8)}...</span>
                                </td>
                                <td className="px-6 py-4 text-sm text-gray-700 max-w-xs">{modReq.reason}</td>
                                <td className="px-6 py-4 text-sm text-gray-600">
                                  <pre className="text-xs whitespace-pre-wrap font-mono bg-gray-50 p-2 rounded">
                                    {changesText || 'Žádné změny'}
                                  </pre>
                                </td>
                                <td className="px-6 py-4 text-sm text-gray-500"><StatusBadge status={modReq.status} /></td>
                                <td className="px-6 py-4 text-sm font-medium">
                                  {modReq.status === 'pending' && (
                                    <div className="flex space-x-2">
                                      <button onClick={() => handleModificationRequest(modReq.id, true)} className="text-green-600 hover:text-green-900">Schválit</button>
                                      <button onClick={() => handleModificationRequest(modReq.id, false)} className="text-red-600 hover:text-red-900">Zamítnout</button>
                                    </div>
                                  )}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                      {modificationRequests.length === 0 && (
                        <p className="text-center text-gray-500 py-4">Žádné žádosti o změnu</p>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </main>
      </div>

      {/* Modály */}
      <ProfileModal isOpen={profileModalOpen} onClose={() => setProfileModalOpen(false)} user={user} />
      <NoteModal
        isOpen={noteModal.isOpen}
        onClose={() => setNoteModal({ isOpen: false, recordId: null, collectionName: '', initialText: '' })}
        onSave={saveNote}
        initialText={noteModal.initialText}
      />
      <EditRequestModal
        isOpen={editModal.isOpen}
        onClose={() => setEditModal({ isOpen: false, request: null })}
        onSave={updatePendingRequest}
        request={editModal.request}
      />
      <ModificationRequestModal
        isOpen={modificationModal.isOpen}
        onClose={() => {
          setModificationModal({ isOpen: false, request: null });
          setModificationReason('');
        }}
        onSubmit={createModificationRequest}
        request={modificationModal.request}
        reason={modificationReason}
        setReason={setModificationReason}
      />
    </>
  );
}
